import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { categoriesAPI, transactionsAPI } from '../services/api';
import {
  captureWithNativeCamera,
  isNativeMobilePlatform,
  pickFromNativeGallery,
} from '../services/nativeReceiptCapture';
import { getTodayBusinessIsoDate, parseBusinessDate } from '../utils/businessDate';
import StatusToast from './StatusToast';
import SearchableSelect from './SearchableSelect';
import ManualTransactionModal from './ManualTransactionModal';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(amount || 0));
};

const normalizeReceiptDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return `${isoMatch[2]}-${isoMatch[3]}-${isoMatch[1]}`;
  }

  const isoSlashMatch = raw.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
  if (isoSlashMatch) {
    return `${isoSlashMatch[2]}-${isoSlashMatch[3]}-${isoSlashMatch[1]}`;
  }

  const usMatch = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (usMatch) {
    return `${usMatch[1].padStart(2, '0')}-${usMatch[2].padStart(2, '0')}-${usMatch[3]}`;
  }

  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) {
    return digits;
  }
  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  }
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
};

const parseMmDdYyyyToDate = (value) => {
  const normalized = normalizeReceiptDate(value);
  const match = normalized.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!match) {
    return null;
  }

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

const formatDateAsMmDdYyyy = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = String(date.getFullYear());
  return `${month}-${day}-${year}`;
};

const formatDateForDisplay = (value) => {
  const date = parseMmDdYyyyToDate(value);
  if (!date) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const formatRecurrencePreviewDate = (value) => {
  const date = parseBusinessDate(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};
const getTodayIsoDate = () => getTodayBusinessIsoDate();

const parseCurrency = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseQuantity = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const calculateTotals = (items, taxAmount, initialTotals = null) => {
  const itemsSubtotal = items.reduce((sum, item) => sum + parseCurrency(item.itemTotal), 0);
  const hasInitialSubtotal = initialTotals && Number.isFinite(parseCurrency(initialTotals.subtotal));
  const subtotal = hasInitialSubtotal && parseCurrency(initialTotals.subtotal) > 0
    ? parseCurrency(initialTotals.subtotal)
    : itemsSubtotal;
  let tax = parseCurrency(taxAmount);

  const hasInitialTotal = initialTotals && Number.isFinite(parseCurrency(initialTotals.total));
  if (hasInitialTotal && parseCurrency(initialTotals.total) > 0) {
    const initialTotal = parseCurrency(initialTotals.total);
    if (Math.abs(initialTotal - subtotal) <= 0.05 && tax > 0) {
      // Receipt total already includes item sums; avoid adding tax twice.
      tax = 0;
    }
  }

  return {
    subtotal: Number(subtotal.toFixed(2)),
    total: Number((subtotal + tax).toFixed(2)),
  };
};

const buildCollapseKey = (item) => {
  const normalizedName = String(item.itemName || '')
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const normalizedCategory = item.categoryId || item.suggestedCategoryId || '';
  const priceBucket = Number(parseCurrency(item.unitPrice || 0)).toFixed(2);
  const sign = parseCurrency(item.itemTotal || 0) < 0 ? 'NEG' : 'POS';

  return `${normalizedName}|${normalizedCategory}|${priceBucket}|${sign}`;
};

const buildCollapsedItems = (items) => {
  const groups = new Map();

  items.forEach((item, index) => {
    const key = buildCollapseKey(item);
    if (!groups.has(key)) {
      groups.set(key, {
        ...item,
        quantity: parseQuantity(item.quantity),
        itemTotal: parseCurrency(item.itemTotal),
        confidence: Number(item.confidence || 0),
        sourceIndices: [index],
      });
      return;
    }

    const existing = groups.get(key);
    existing.quantity += parseQuantity(item.quantity);
    existing.itemTotal = Number((existing.itemTotal + parseCurrency(item.itemTotal)).toFixed(2));
    existing.confidence = Math.max(Number(existing.confidence || 0), Number(item.confidence || 0));
    existing.sourceIndices.push(index);
  });

  return Array.from(groups.values());
};

const toDisplayItemName = (value) => {
  const normalized = String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

  if (!normalized) {
    return '';
  }

  return normalized
    .split(' ')
    .map((word) =>
      word
        .split('-')
        .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
        .join('-')
    )
    .join(' ');
};

const createManualItem = () => ({
  itemName: '',
  itemTotal: '',
  categoryId: '',
  quantity: '',
  unitPrice: '',
  itemTotalOverridden: false,
});

const DEFAULT_NEW_CATEGORY_COLOR = '#3b82f6';
const ADD_PARENT_CATEGORY_OPTION = '__add_parent_category__';
const CATEGORY_COLOR_OPTIONS = [
  { name: 'Green', value: '#10b981' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Orange', value: '#f59e0b' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Gray', value: '#64748b' },
];

const toComparableText = (value) => String(value || '').toLowerCase().trim();
const normalizeHexColor = (value) => String(value || '').trim().toLowerCase();
const hslToHex = (h, s, l) => {
  const saturation = s / 100;
  const lightness = l / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lightness - chroma / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) {
    r = chroma; g = x; b = 0;
  } else if (h < 120) {
    r = x; g = chroma; b = 0;
  } else if (h < 180) {
    r = 0; g = chroma; b = x;
  } else if (h < 240) {
    r = 0; g = x; b = chroma;
  } else if (h < 300) {
    r = x; g = 0; b = chroma;
  } else {
    r = chroma; g = 0; b = x;
  }

  const toHex = (channel) => Math.round((channel + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};
const getNextUniqueCategoryColor = (takenColors, palette = CATEGORY_COLOR_OPTIONS) => {
  const normalizedTaken = new Set(Array.from(takenColors || []).map(normalizeHexColor));
  const paletteColor = palette.find((color) => !normalizedTaken.has(normalizeHexColor(color.value)));
  if (paletteColor) {
    return paletteColor.value;
  }

  for (let hue = 0; hue < 360; hue += 11) {
    const generated = hslToHex(hue, 70, 52);
    if (!normalizedTaken.has(normalizeHexColor(generated))) {
      return generated;
    }
  }

  return DEFAULT_NEW_CATEGORY_COLOR;
};
const getAutoCalculatedItemTotal = (item) => {
  const quantity = item?.quantity === '' ? 1 : Number(item?.quantity);
  const unitPrice = Number(item?.unitPrice);
  if (Number.isFinite(quantity) && quantity > 0 && Number.isFinite(unitPrice) && unitPrice >= 0) {
    return Number((quantity * unitPrice).toFixed(2));
  }

  return null;
};

const resolveManualItemTotal = (item) => {
  const manualTotal = Number(item?.itemTotal);
  const autoCalculatedTotal = getAutoCalculatedItemTotal(item);

  if (item?.itemTotalOverridden) {
    if (Number.isFinite(manualTotal) && manualTotal > 0) {
      return Number(manualTotal.toFixed(2));
    }
    return null;
  }

  if (autoCalculatedTotal !== null) {
    return autoCalculatedTotal;
  }

  if (Number.isFinite(manualTotal) && manualTotal > 0) {
    return Number(manualTotal.toFixed(2));
  }

  return null;
};

const UI_DRAFT_KEY = 'upload-receipt-ui-draft-v1';
const RECENT_CATEGORY_IDS_KEY = 'upload-receipt-recent-category-ids-v1';
const MAX_UPLOAD_IMAGE_EDGE = 2000;
const OUTPUT_JPEG_QUALITY = 0.82;
const MIN_RECEIPT_LONG_EDGE = 900;
const RECURRENCE_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semiannual', label: 'Semiannual' },
  { value: 'annual', label: 'Annual' },
];
const RECURRENCE_FREQUENCY_LABELS = RECURRENCE_FREQUENCY_OPTIONS.reduce((labels, option) => {
  labels[option.value] = option.label;
  return labels;
}, {});

const createEmptyManualFieldErrors = () => ({
  transactionDate: '',
  storeName: '',
  paymentMethod: '',
  taxAmount: '',
  items: {},
});

const hasMeaningfulManualItem = (item) => {
  if (!item || typeof item !== 'object') {
    return false;
  }

  return Boolean(
    String(item.itemName || '').trim() ||
    String(item.categoryId || '').trim() ||
    String(item.itemTotal || '').trim() ||
    String(item.quantity || '').trim() ||
    String(item.unitPrice || '').trim()
  );
};

const hasMeaningfulManualFormData = (form) => {
  if (!form || typeof form !== 'object') {
    return false;
  }

  // `transactionDate` defaults to today's date, so date-only state should not
  // be treated as a user-created draft.
  if (
    String(form.storeName || '').trim() ||
    String(form.storeLocation || '').trim() ||
    String(form.taxAmount || '').trim() ||
    String(form.paymentMethod || '').trim() ||
    Boolean(form.isRecurring) ||
    String(form.recurrenceFrequency || '').trim() ||
    String(form.recurrenceStartDate || '').trim() ||
    String(form.recurrenceEndDate || '').trim()
  ) {
    return true;
  }

  const items = Array.isArray(form.items) ? form.items : [];
  return items.some((item) => hasMeaningfulManualItem(item));
};

const hasMeaningfulDraftData = (draft) => {
  const hasExtractedData =
    Boolean(draft?.extractedData) &&
    typeof draft.extractedData === 'object' &&
    Object.keys(draft.extractedData).length > 0;

  return hasExtractedData || hasMeaningfulManualFormData(draft?.manualForm);
};

const buildImageQualityBadges = (warningText) => {
  const text = String(warningText || '').toLowerCase();
  const badges = [];
  if (!text) {
    return badges;
  }
  if (text.includes('resolution')) {
    badges.push('Low resolution');
  }
  if (text.includes('dark')) {
    badges.push('Low light');
  }
  if (text.includes('blurry')) {
    badges.push('Blurry');
  }
  return badges.length ? badges : ['Review quality'];
};

const readFileAsDataUrl = (file) => (
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  })
);

const loadImageElement = (file) => (
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to decode image'));
    };
    image.src = objectUrl;
  })
);

const computeImageQualitySignals = (canvas) => {
  const sampleCanvas = document.createElement('canvas');
  const sampleContext = sampleCanvas.getContext('2d');
  if (!sampleContext) {
    return { meanBrightness: 128, edgeDensity: 100 };
  }
  const maxSampleEdge = 96;
  const scale = Math.min(1, maxSampleEdge / Math.max(canvas.width, canvas.height));
  sampleCanvas.width = Math.max(24, Math.floor(canvas.width * scale));
  sampleCanvas.height = Math.max(24, Math.floor(canvas.height * scale));
  sampleContext.drawImage(canvas, 0, 0, sampleCanvas.width, sampleCanvas.height);

  const { data } = sampleContext.getImageData(0, 0, sampleCanvas.width, sampleCanvas.height);
  const grayscale = new Array(sampleCanvas.width * sampleCanvas.height);
  let brightnessSum = 0;

  for (let i = 0, pixelIndex = 0; i < data.length; i += 4, pixelIndex += 1) {
    const luminance = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    grayscale[pixelIndex] = luminance;
    brightnessSum += luminance;
  }

  const meanBrightness = brightnessSum / grayscale.length;
  let edgeSum = 0;

  for (let y = 1; y < sampleCanvas.height - 1; y += 1) {
    for (let x = 1; x < sampleCanvas.width - 1; x += 1) {
      const idx = y * sampleCanvas.width + x;
      const laplacian = (
        -4 * grayscale[idx] +
        grayscale[idx - 1] +
        grayscale[idx + 1] +
        grayscale[idx - sampleCanvas.width] +
        grayscale[idx + sampleCanvas.width]
      );
      edgeSum += Math.abs(laplacian);
    }
  }

  const edgeDensity = edgeSum / grayscale.length;
  return { meanBrightness, edgeDensity };
};

const createCanvas = (width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
};

const getCanvasContext = (canvas) => canvas.getContext('2d', { willReadFrequently: true });

const applyGlareReduction = (canvas) => {
  const context = getCanvasContext(canvas);
  if (!context) {
    return false;
  }

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;
  let glarePixels = 0;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    const saturation = max === 0 ? 0 : (max - min) / max;

    // Compress bright low-saturation highlights (typical glare patches).
    if (luminance > 210 && saturation < 0.22) {
      const compression = luminance > 240 ? 0.42 : luminance > 225 ? 0.52 : 0.65;
      data[i] = Math.max(0, Math.min(255, Math.round(220 + (r - 220) * compression)));
      data[i + 1] = Math.max(0, Math.min(255, Math.round(220 + (g - 220) * compression)));
      data[i + 2] = Math.max(0, Math.min(255, Math.round(220 + (b - 220) * compression)));
      glarePixels += 1;
      continue;
    }

    // Mild global highlight roll-off for non-glare bright zones.
    if (luminance > 230) {
      data[i] = Math.round(225 + (r - 225) * 0.72);
      data[i + 1] = Math.round(225 + (g - 225) * 0.72);
      data[i + 2] = Math.round(225 + (b - 225) * 0.72);
    }
  }

  context.putImageData(imageData, 0, 0);
  const totalPixels = canvas.width * canvas.height;
  return totalPixels > 0 && glarePixels / totalPixels >= 0.01;
};

const findActiveRange = (scores, threshold, minSpan) => {
  let start = -1;
  let end = -1;
  for (let index = 0; index < scores.length; index += 1) {
    if (scores[index] >= threshold) {
      if (start === -1) {
        start = index;
      }
      end = index;
    }
  }
  if (start === -1 || end === -1 || end - start + 1 < minSpan) {
    return null;
  }
  return { start, end };
};

const detectReceiptBounds = (canvas) => {
  const maxSampleEdge = 320;
  const scale = Math.min(1, maxSampleEdge / Math.max(canvas.width, canvas.height));
  const sample = createCanvas(canvas.width * scale, canvas.height * scale);
  const sampleContext = getCanvasContext(sample);
  if (!sampleContext) {
    return null;
  }
  sampleContext.drawImage(canvas, 0, 0, sample.width, sample.height);
  const { data } = sampleContext.getImageData(0, 0, sample.width, sample.height);
  const width = sample.width;
  const height = sample.height;

  const rowScores = new Array(height).fill(0);
  const colScores = new Array(width).fill(0);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      const luminance = 0.299 * data[index] + 0.587 * data[index + 1] + 0.114 * data[index + 2];
      const darkWeight = luminance < 230 ? (230 - luminance) / 230 : 0;
      rowScores[y] += darkWeight;
      colScores[x] += darkWeight;
    }
  }

  const normalizedRowScores = rowScores.map((score) => score / width);
  const normalizedColScores = colScores.map((score) => score / height);

  const rowThreshold = Math.max(0.018, Math.min(0.075, normalizedRowScores.reduce((sum, v) => sum + v, 0) / height * 1.1));
  const colThreshold = Math.max(0.018, Math.min(0.075, normalizedColScores.reduce((sum, v) => sum + v, 0) / width * 1.1));

  const rowRange = findActiveRange(normalizedRowScores, rowThreshold, Math.max(12, Math.round(height * 0.22)));
  const colRange = findActiveRange(normalizedColScores, colThreshold, Math.max(12, Math.round(width * 0.2)));
  if (!rowRange || !colRange) {
    return null;
  }

  const padX = Math.round((colRange.end - colRange.start + 1) * 0.04);
  const padY = Math.round((rowRange.end - rowRange.start + 1) * 0.04);
  const left = Math.max(0, colRange.start - padX);
  const top = Math.max(0, rowRange.start - padY);
  const right = Math.min(width - 1, colRange.end + padX);
  const bottom = Math.min(height - 1, rowRange.end + padY);
  const croppedWidth = right - left + 1;
  const croppedHeight = bottom - top + 1;

  if (croppedWidth < width * 0.35 || croppedHeight < height * 0.35) {
    return null;
  }

  const areaRatio = (croppedWidth * croppedHeight) / (width * height);
  if (areaRatio > 0.985) {
    return null;
  }

  const inverseScale = 1 / scale;
  return {
    x: Math.max(0, Math.floor(left * inverseScale)),
    y: Math.max(0, Math.floor(top * inverseScale)),
    width: Math.min(canvas.width, Math.ceil(croppedWidth * inverseScale)),
    height: Math.min(canvas.height, Math.ceil(croppedHeight * inverseScale)),
    areaRatio,
  };
};

const cropCanvas = (canvas, bounds) => {
  if (!bounds || !bounds.width || !bounds.height) {
    return canvas;
  }

  const next = createCanvas(bounds.width, bounds.height);
  const context = getCanvasContext(next);
  if (!context) {
    return canvas;
  }
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, next.width, next.height);
  context.drawImage(
    canvas,
    bounds.x,
    bounds.y,
    bounds.width,
    bounds.height,
    0,
    0,
    next.width,
    next.height
  );
  return next;
};

const estimateSkewAngle = (canvas) => {
  const maxEdge = 760;
  const scale = Math.min(1, maxEdge / Math.max(canvas.width, canvas.height));
  const sample = createCanvas(canvas.width * scale, canvas.height * scale);
  const sampleContext = getCanvasContext(sample);
  if (!sampleContext) {
    return 0;
  }
  sampleContext.drawImage(canvas, 0, 0, sample.width, sample.height);
  const imageData = sampleContext.getImageData(0, 0, sample.width, sample.height);
  const { data } = imageData;
  const width = sample.width;
  const height = sample.height;

  let luminanceSum = 0;
  const luminances = new Array(width * height);
  for (let i = 0, pixelIndex = 0; i < data.length; i += 4, pixelIndex += 1) {
    const luminance = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    luminances[pixelIndex] = luminance;
    luminanceSum += luminance;
  }
  const meanLuminance = luminanceSum / Math.max(1, luminances.length);
  const threshold = Math.max(65, Math.min(210, meanLuminance - 22));

  const points = [];
  const step = Math.max(1, Math.round(Math.max(width, height) / 360));
  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      const luminance = luminances[y * width + x];
      if (luminance <= threshold) {
        points.push([x, y]);
      }
    }
  }

  if (points.length < 350) {
    return 0;
  }

  let bestAngle = 0;
  let bestScore = -Infinity;
  for (let angle = -6; angle <= 6; angle += 0.5) {
    const radians = angle * (Math.PI / 180);
    const sin = Math.sin(radians);
    const cos = Math.cos(radians);
    const bins = new Array(height).fill(0);
    for (let i = 0; i < points.length; i += 1) {
      const point = points[i];
      const projectedY = Math.round(point[0] * sin + point[1] * cos);
      if (projectedY >= 0 && projectedY < height) {
        bins[projectedY] += 1;
      }
    }

    let energy = 0;
    for (let i = 0; i < bins.length; i += 1) {
      const count = bins[i];
      energy += count * count;
    }

    if (energy > bestScore) {
      bestScore = energy;
      bestAngle = angle;
    }
  }

  return Math.abs(bestAngle) >= 0.4 ? bestAngle : 0;
};

const rotateCanvas = (canvas, angleDegrees) => {
  if (!angleDegrees) {
    return canvas;
  }

  const radians = angleDegrees * (Math.PI / 180);
  const sin = Math.abs(Math.sin(radians));
  const cos = Math.abs(Math.cos(radians));
  const nextWidth = Math.max(1, Math.ceil(canvas.width * cos + canvas.height * sin));
  const nextHeight = Math.max(1, Math.ceil(canvas.width * sin + canvas.height * cos));

  const next = createCanvas(nextWidth, nextHeight);
  const context = getCanvasContext(next);
  if (!context) {
    return canvas;
  }
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, next.width, next.height);
  context.translate(next.width / 2, next.height / 2);
  context.rotate(radians);
  context.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
  return next;
};

const preprocessReceiptImage = async (file) => {
  if (!file || !String(file.type || '').startsWith('image/')) {
    return { file, qualityWarnings: [] };
  }

  const image = await loadImageElement(file);
  const originalLongEdge = Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height);
  const scale = Math.min(1, MAX_UPLOAD_IMAGE_EDGE / Math.max(image.width, image.height));
  const targetWidth = Math.max(1, Math.round(image.width * scale));
  const targetHeight = Math.max(1, Math.round(image.height * scale));

  let canvas = createCanvas(targetWidth, targetHeight);
  const context = getCanvasContext(canvas);
  if (!context) {
    return { file, qualityWarnings: [] };
  }
  context.drawImage(image, 0, 0, targetWidth, targetHeight);

  const qualityWarnings = [];
  const glareDetected = applyGlareReduction(canvas);
  if (glareDetected) {
    qualityWarnings.push('Detected glare hotspots; highlight reduction applied.');
  }

  const firstBounds = detectReceiptBounds(canvas);
  if (firstBounds) {
    canvas = cropCanvas(canvas, firstBounds);
  }

  const skewAngle = estimateSkewAngle(canvas);
  if (skewAngle) {
    canvas = rotateCanvas(canvas, skewAngle);
    const secondBounds = detectReceiptBounds(canvas);
    if (secondBounds) {
      canvas = cropCanvas(canvas, secondBounds);
    }
  }

  const { meanBrightness, edgeDensity } = computeImageQualitySignals(canvas);
  if (originalLongEdge < MIN_RECEIPT_LONG_EDGE) {
    qualityWarnings.push('Image resolution is low; OCR quality may drop.');
  }
  if (meanBrightness < 65) {
    qualityWarnings.push('Image looks dark; consider retaking with better lighting.');
  }
  if (edgeDensity < 18) {
    qualityWarnings.push('Image may be blurry; retaking could improve extraction.');
  }

  const optimizedBlob = await new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Image compression failed'));
        return;
      }
      resolve(blob);
    }, 'image/jpeg', OUTPUT_JPEG_QUALITY);
  });

  const optimizedName = String(file.name || 'receipt.jpg').replace(/\.[^.]+$/, '.jpg');
  const optimizedFile = new File([optimizedBlob], optimizedName, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });

  return {
    file: optimizedFile,
    qualityWarnings,
  };
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const isPointInsideRect = (x, y, rect) => (
  x >= rect.x &&
  x <= rect.x + rect.width &&
  y >= rect.y &&
  y <= rect.y + rect.height
);

function UploadReceipt() {
  const navigate = useNavigate();
  const cameraInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const manualModalRef = useRef(null);
  const draftWriteEnabledRef = useRef(false);
  const draftWriteTimeoutRef = useRef(null);
  const cropImageRef = useRef(null);
  const cropSurfaceRef = useRef(null);
  const cropDragRef = useRef(null);
  const actionFeedbackDebounceRef = useRef(null);
  const actionFeedbackHideRef = useRef(null);

  const [uploadedFile, setUploadedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingReviewItem, setEditingReviewItem] = useState(null);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [newCategoryForm, setNewCategoryForm] = useState({
    name: '',
    parentCategoryId: '',
  });
  const [isCreateCategoryNameFocused, setIsCreateCategoryNameFocused] = useState(false);
  const [isCreateCategorySelectedFromSuggestion, setIsCreateCategorySelectedFromSuggestion] = useState(false);
  const [newParentSubcategories, setNewParentSubcategories] = useState([]);
  const [newSubcategoryInput, setNewSubcategoryInput] = useState('');
  const [showAllCreateSubcategoryRefs, setShowAllCreateSubcategoryRefs] = useState(false);
  const [createCategoryContext, setCreateCategoryContext] = useState({
    source: 'editor',
    manualItemIndex: null,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [creatingTransaction, setCreatingTransaction] = useState(false);
  const [manualFormError, setManualFormError] = useState('');
  const [isManualDetailsOpen, setIsManualDetailsOpen] = useState(false);
  const [isManualMobileView, setIsManualMobileView] = useState(() => window.innerWidth < 768);
  const [manualForm, setManualForm] = useState({
    transactionDate: getTodayIsoDate(),
    storeName: '',
    storeLocation: '',
    taxAmount: '',
    paymentMethod: '',
    isRecurring: false,
    recurrenceFrequency: 'monthly',
    recurrenceStartDate: '',
    recurrenceEndDate: '',
    items: [createManualItem()],
  });
  const [manualFieldErrors, setManualFieldErrors] = useState(createEmptyManualFieldErrors);
  const [imageQualityWarning, setImageQualityWarning] = useState('');
  const [pendingDraft, setPendingDraft] = useState(null);
  const [processingStage, setProcessingStage] = useState('idle');
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [duplicateSaveConfirmed, setDuplicateSaveConfirmed] = useState(false);
  const [duplicateConfirmContext, setDuplicateConfirmContext] = useState('upload');
  const [saveSummary, setSaveSummary] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropRect, setCropRect] = useState(null);
  const [cropDisplay, setCropDisplay] = useState({ width: 0, height: 0 });
  const [showReplaceOptions, setShowReplaceOptions] = useState(false);
  const [actionFeedback, setActionFeedback] = useState('');
  const [isReceiptMetaEditing, setIsReceiptMetaEditing] = useState(false);
  const [isTaxEditing, setIsTaxEditing] = useState(false);
  const [showSubcategorySuggestionModal, setShowSubcategorySuggestionModal] = useState(false);
  const [subcategorySuggestions, setSubcategorySuggestions] = useState([]);
  const [applyingSubcategorySuggestions, setApplyingSubcategorySuggestions] = useState(false);
  const [recentCategoryIds, setRecentCategoryIds] = useState([]);

  useEffect(() => {
    const handleViewportUpdate = () => {
      setIsManualMobileView(window.innerWidth < 768);
    };

    handleViewportUpdate();
    window.addEventListener('resize', handleViewportUpdate);
    return () => {
      window.removeEventListener('resize', handleViewportUpdate);
    };
  }, []);

  useEffect(() => {
    try {
      const rawDraft = sessionStorage.getItem(UI_DRAFT_KEY);
      if (!rawDraft) {
        draftWriteEnabledRef.current = true;
        return;
      }

      const draft = JSON.parse(rawDraft);
      const hasDraftData = hasMeaningfulDraftData(draft);
      if (hasDraftData) {
        setPendingDraft(draft);
      } else {
        sessionStorage.removeItem(UI_DRAFT_KEY);
        draftWriteEnabledRef.current = true;
      }
    } catch (draftError) {
      console.warn('Failed to restore upload receipt draft:', draftError);
      draftWriteEnabledRef.current = true;
    }
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_CATEGORY_IDS_KEY);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setRecentCategoryIds(parsed.map((value) => String(value)).filter(Boolean).slice(0, 5));
      }
    } catch (storageError) {
      console.warn('Failed to restore recent categories:', storageError);
    }
  }, []);

  useEffect(() => {
    if (!draftWriteEnabledRef.current) {
      return;
    }

    if (draftWriteTimeoutRef.current) {
      clearTimeout(draftWriteTimeoutRef.current);
    }

    const draft = {
      extractedData,
      manualForm,
    };
    const shouldPersistDraft = hasMeaningfulDraftData(draft);

    draftWriteTimeoutRef.current = setTimeout(() => {
      try {
        if (shouldPersistDraft) {
          sessionStorage.setItem(UI_DRAFT_KEY, JSON.stringify(draft));
        } else {
          sessionStorage.removeItem(UI_DRAFT_KEY);
        }
      } catch (draftError) {
        console.warn('Failed to save upload receipt draft:', draftError);
      }
    }, 350);

    return () => {
      if (draftWriteTimeoutRef.current) {
        clearTimeout(draftWriteTimeoutRef.current);
      }
    };
  }, [extractedData, manualForm]);

  useEffect(() => () => {
    if (draftWriteTimeoutRef.current) {
      clearTimeout(draftWriteTimeoutRef.current);
    }
  }, []);

  useEffect(() => () => {
    if (actionFeedbackDebounceRef.current) {
      clearTimeout(actionFeedbackDebounceRef.current);
    }
    if (actionFeedbackHideRef.current) {
      clearTimeout(actionFeedbackHideRef.current);
    }
  }, []);

  const showDebouncedActionFeedback = useCallback((message) => {
    if (actionFeedbackDebounceRef.current) {
      clearTimeout(actionFeedbackDebounceRef.current);
    }
    if (actionFeedbackHideRef.current) {
      clearTimeout(actionFeedbackHideRef.current);
    }
    setActionFeedback('');
    actionFeedbackDebounceRef.current = setTimeout(() => {
      setActionFeedback(message);
      actionFeedbackHideRef.current = setTimeout(() => {
        setActionFeedback('');
      }, 1600);
    }, 220);
  }, []);

  const handleRestoreDraft = () => {
    if (pendingDraft?.extractedData && typeof pendingDraft.extractedData === 'object') {
      setExtractedData(pendingDraft.extractedData);
    }
    if (pendingDraft?.manualForm && typeof pendingDraft.manualForm === 'object' && Array.isArray(pendingDraft.manualForm.items)) {
      setManualForm({
        transactionDate: pendingDraft.manualForm.transactionDate ?? getTodayIsoDate(),
        storeName: pendingDraft.manualForm.storeName ?? '',
        storeLocation: pendingDraft.manualForm.storeLocation ?? '',
        taxAmount: pendingDraft.manualForm.taxAmount ?? '',
        paymentMethod: pendingDraft.manualForm.paymentMethod ?? '',
        isRecurring: Boolean(pendingDraft.manualForm.isRecurring),
        recurrenceFrequency: pendingDraft.manualForm.recurrenceFrequency ?? 'monthly',
        recurrenceStartDate: pendingDraft.manualForm.recurrenceStartDate ?? '',
        recurrenceEndDate: pendingDraft.manualForm.recurrenceEndDate ?? '',
        items: pendingDraft.manualForm.items.length ? pendingDraft.manualForm.items : [createManualItem()],
      });
    }
    setPendingDraft(null);
    setIsReceiptMetaEditing(false);
    draftWriteEnabledRef.current = true;
  };

  const handleDiscardDraft = () => {
    setPendingDraft(null);
    sessionStorage.removeItem(UI_DRAFT_KEY);
    draftWriteEnabledRef.current = true;
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const data = await categoriesAPI.getAll();
        setCategories(data);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories. Please refresh and try again.');
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const setSelectedReceiptFile = useCallback(async (file, options = {}) => {
    const { autoOpenCrop = true } = options;
    if (!file) {
      return;
    }

    // A newly selected image means the user is starting a fresh upload flow.
    setPendingDraft(null);
    sessionStorage.removeItem(UI_DRAFT_KEY);
    draftWriteEnabledRef.current = true;

    setError('');
    setSuccess('');
    setActionFeedback('');
    setBudgetSummary(null);
    setExtractedData(null);

    try {
      const processed = await preprocessReceiptImage(file);
      const nextFile = processed.file || file;
      setUploadedFile(nextFile);
      setImageQualityWarning((processed.qualityWarnings || []).join(' '));
      const previewUrl = await readFileAsDataUrl(nextFile);
      setPreview(previewUrl);
      if (autoOpenCrop) {
        setCropRect(null);
        setCropDisplay({ width: 0, height: 0 });
        setShowCropModal(true);
      }
    } catch (processingError) {
      console.warn('Client image preprocessing failed; using original file:', processingError);
      setUploadedFile(file);
      setImageQualityWarning('');
      const previewUrl = await readFileAsDataUrl(file);
      setPreview(previewUrl);
      if (autoOpenCrop) {
        setCropRect(null);
        setCropDisplay({ width: 0, height: 0 });
        setShowCropModal(true);
      }
    }
  }, []);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) {
      return;
    }

    await setSelectedReceiptFile(file);
  }, [setSelectedReceiptFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
    },
    maxFiles: 1,
  });

  const handleProcessReceipt = async () => {
    if (!uploadedFile) {
      setError('Upload a receipt image first.');
      return;
    }

    setError('');
    setSuccess('');
    setBudgetSummary(null);
    setIsProcessing(true);
    setProcessingStage('uploading');
    const extractingTimer = setTimeout(() => setProcessingStage('extracting'), 600);
    const categorizingTimer = setTimeout(() => setProcessingStage('categorizing'), 1800);

    try {
      const receiptDraft = await transactionsAPI.processReceipt(uploadedFile);
      const reviewedItems = (receiptDraft.items || []).map((item) => ({
        ...item,
        categoryId: item.suggestedCategoryId || defaultCategoryId,
      }));

      const derivedTotals = calculateTotals(reviewedItems, receiptDraft.taxAmount, {
        subtotal: receiptDraft.subtotal,
        total: receiptDraft.total,
      });

      setExtractedData({
        ...receiptDraft,
        transactionDate: normalizeReceiptDate(receiptDraft.transactionDate),
        items: reviewedItems,
        subtotal: Number.isFinite(parseCurrency(receiptDraft.subtotal))
          ? parseCurrency(receiptDraft.subtotal)
          : derivedTotals.subtotal,
        total: Number.isFinite(parseCurrency(receiptDraft.total))
          ? parseCurrency(receiptDraft.total)
          : derivedTotals.total,
      });
      setIsReceiptMetaEditing(false);
      setDuplicateSaveConfirmed(false);
    } catch (err) {
      console.error('Error processing receipt:', err);
      const backendError = err.response?.data?.error;
      const backendDetails = err.response?.data?.details;
      setError(
        backendDetails
          ? `${backendError || 'Failed to process receipt'}. ${backendDetails}`
          : backendError || 'Failed to process receipt'
      );
    } finally {
      clearTimeout(extractingTimer);
      clearTimeout(categorizingTimer);
      setProcessingStage('idle');
      setIsProcessing(false);
    }
  };

  const reviewItems = useMemo(() => {
    if (!extractedData?.items) {
      return [];
    }
    return buildCollapsedItems(extractedData.items);
  }, [extractedData]);

  const categoriesById = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      map.set(String(category.id), category);
    });
    return map;
  }, [categories]);

  const topLevelCategories = useMemo(
    () =>
      categories
        .filter((category) => !category.parentCategoryId)
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [categories]
  );

  const childCategoriesByParentId = useMemo(() => {
    const map = new Map();
    categories
      .filter((category) => category.parentCategoryId)
      .forEach((category) => {
        const parentId = String(category.parentCategoryId);
        if (!map.has(parentId)) {
          map.set(parentId, []);
        }
        map.get(parentId).push(category);
      });

    map.forEach((list, parentId) => {
      map.set(parentId, [...list].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))));
    });

    return map;
  }, [categories]);

  const createCategoryNameSuggestions = useMemo(() => {
    const query = toComparableText(newCategoryForm.name);
    return topLevelCategories
      .filter((category) => !query || toComparableText(category.name).includes(query))
      .slice(0, 6);
  }, [topLevelCategories, newCategoryForm.name]);
  const matchedMainCategoryForCreate = useMemo(() => {
    const selectedParentCategoryId = String(newCategoryForm.parentCategoryId || '').trim();
    if (selectedParentCategoryId) {
      return null;
    }
    const query = toComparableText(newCategoryForm.name);
    if (!query) {
      return null;
    }
    return topLevelCategories.find((category) => toComparableText(category.name) === query) || null;
  }, [topLevelCategories, newCategoryForm.name, newCategoryForm.parentCategoryId]);
  const existingCreateSubcategories = useMemo(
    () => (matchedMainCategoryForCreate ? (childCategoriesByParentId.get(String(matchedMainCategoryForCreate.id)) || []) : []),
    [matchedMainCategoryForCreate, childCategoriesByParentId]
  );
  const visibleCreateSubcategoryRefs = useMemo(
    () => (showAllCreateSubcategoryRefs ? existingCreateSubcategories : existingCreateSubcategories.slice(0, 5)),
    [showAllCreateSubcategoryRefs, existingCreateSubcategories]
  );
  const hiddenCreateSubcategoryRefCount = Math.max(existingCreateSubcategories.length - visibleCreateSubcategoryRefs.length, 0);

  const categoryDisplayNameById = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      if (category.parentCategoryId) {
        const parent = categoriesById.get(String(category.parentCategoryId));
        if (parent) {
          map.set(String(category.id), `${parent.name} > ${category.name}`);
          return;
        }
      }
      map.set(String(category.id), category.name);
    });
    return map;
  }, [categories, categoriesById]);

  const orderedCategoryOptions = useMemo(() => {
    const options = [];
    topLevelCategories.forEach((parent) => {
      options.push(parent);
      const children = childCategoriesByParentId.get(String(parent.id)) || [];
      options.push(...children);
    });

    const addedIds = new Set(options.map((category) => String(category.id)));
    const orphanedCategories = categories
      .filter((category) => !addedIds.has(String(category.id)))
      .sort((a, b) => {
        const parentA = categoriesById.get(String(a.parentCategoryId || ''));
        const parentB = categoriesById.get(String(b.parentCategoryId || ''));
        const parentCompare = String(parentA?.name || '').localeCompare(String(parentB?.name || ''));
        if (parentCompare !== 0) {
          return parentCompare;
        }
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
    options.push(...orphanedCategories);

    return options;
  }, [categories, topLevelCategories, childCategoriesByParentId, categoriesById]);

  const categorySelectOptions = useMemo(
    () => ([
      { value: '', label: 'Category *' },
      ...orderedCategoryOptions.map((category) => {
        const categoryId = String(category.id);
        if (category.parentCategoryId) {
          const parent = categoriesById.get(String(category.parentCategoryId));
          return {
            value: categoryId,
            label: category.name,
            description: parent?.name || 'Subcategory',
            indent: true,
            searchText: `${parent?.name || ''} ${category.name}`,
          };
        }
        return {
          value: categoryId,
          label: category.name,
          searchText: category.name,
        };
      }),
      { value: ADD_PARENT_CATEGORY_OPTION, label: '+ Add Category', searchText: 'add category new category' },
    ]),
    [orderedCategoryOptions, categoriesById]
  );

  const parentCategoryReferenceOptions = useMemo(
    () => ([
      { value: '', label: 'Select parent category' },
      ...topLevelCategories.map((category) => ({ value: String(category.id), label: category.name })),
    ]),
    [topLevelCategories]
  );

  const defaultCategoryId = useMemo(() => {
    const preferred = orderedCategoryOptions.find((category) => {
      if (!category || category.parentCategoryId) {
        return false;
      }
      return toComparableText(category.name).replace(/[^a-z0-9]/g, '') === 'other';
    });
    return String(preferred?.id || orderedCategoryOptions[0]?.id || '');
  }, [orderedCategoryOptions]);

  const recentCategoryChips = useMemo(
    () =>
      recentCategoryIds
        .map((categoryId) => categoriesById.get(String(categoryId)))
        .filter(Boolean)
        .slice(0, 5),
    [categoriesById, recentCategoryIds]
  );

  const rememberRecentCategory = useCallback((categoryId) => {
    const normalizedId = String(categoryId || '').trim();
    if (!normalizedId) {
      return;
    }
    setRecentCategoryIds((current) => {
      const next = [normalizedId, ...current.filter((id) => id !== normalizedId)].slice(0, 5);
      try {
        localStorage.setItem(RECENT_CATEGORY_IDS_KEY, JSON.stringify(next));
      } catch (storageError) {
        console.warn('Failed to persist recent categories:', storageError);
      }
      return next;
    });
  }, []);

  const updateSubcategorySuggestion = (id, updates) => {
    setSubcategorySuggestions((current) =>
      current.map((entry) => (entry.id === id ? { ...entry, ...updates } : entry))
    );
  };

  const applySubcategorySuggestions = async () => {
    const selectedSuggestions = subcategorySuggestions
      .filter((entry) => entry.selected)
      .map((entry) => ({
        ...entry,
        name: String(entry.name || '').trim(),
        parentCategoryId: String(entry.parentCategoryId || '').trim(),
      }))
      .filter((entry) => entry.name && entry.parentCategoryId && Array.isArray(entry.itemIndexes) && entry.itemIndexes.length);

    if (!selectedSuggestions.length) {
      setShowSubcategorySuggestionModal(false);
      return;
    }

    setApplyingSubcategorySuggestions(true);
    setError('');

    try {
      const createdOrExistingCategories = [];
      const assignmentByIndex = new Map();

      for (const suggestion of selectedSuggestions) {
        const existing = categories.find(
          (category) =>
            String(category.parentCategoryId || '') === suggestion.parentCategoryId &&
            toComparableText(category.name) === toComparableText(suggestion.name)
        );

        let targetCategory = existing || null;
        if (!targetCategory) {
          targetCategory = await categoriesAPI.create({
            name: suggestion.name,
            parentCategoryId: suggestion.parentCategoryId,
          });
          createdOrExistingCategories.push(targetCategory);
        }

        suggestion.itemIndexes.forEach((itemIndex) => {
          assignmentByIndex.set(Number(itemIndex), String(targetCategory.id));
        });
      }

      if (createdOrExistingCategories.length) {
        setCategories((current) => {
          const byId = new Map(current.map((category) => [String(category.id), category]));
          createdOrExistingCategories.forEach((category) => byId.set(String(category.id), category));
          return Array.from(byId.values());
        });
      }

      setExtractedData((current) => {
        if (!current?.items) {
          return current;
        }
        const updatedItems = current.items.map((item, index) => {
          const updatedCategoryId = assignmentByIndex.get(index);
          if (!updatedCategoryId) {
            return item;
          }
          return { ...item, categoryId: updatedCategoryId };
        });

        return {
          ...current,
          items: updatedItems,
          ...calculateTotals(updatedItems, current.taxAmount),
        };
      });

      setShowSubcategorySuggestionModal(false);
      setSubcategorySuggestions([]);
      selectedSuggestions.forEach((entry) => {
        const categoryId = assignmentByIndex.get(Number(entry.itemIndexes[0]));
        if (categoryId) {
          rememberRecentCategory(categoryId);
        }
      });
      setSuccess(`Applied ${selectedSuggestions.length} subcategory suggestion${selectedSuggestions.length === 1 ? '' : 's'}.`);
    } catch (applyError) {
      console.error('Failed to apply subcategory suggestions:', applyError);
      setError(applyError.response?.data?.error || 'Failed to apply subcategory suggestions');
    } finally {
      setApplyingSubcategorySuggestions(false);
    }
  };

  const updateReviewItem = (reviewItem, updater) => {
    setExtractedData((current) => {
      if (!current) {
        return current;
      }

      const sourceIndices = (reviewItem.sourceIndices || [])
        .map((index) => Number(index))
        .filter((index) => Number.isInteger(index))
        .sort((a, b) => a - b);

      if (!sourceIndices.length) {
        return current;
      }

      const [primaryIndex, ...duplicateIndices] = sourceIndices;
      const duplicateSet = new Set(duplicateIndices);
      const baseItem = current.items[primaryIndex];

      if (!baseItem) {
        return current;
      }

      const nextItem = updater({
        ...baseItem,
        itemName: reviewItem.itemName,
        quantity: parseQuantity(reviewItem.quantity),
        itemTotal: parseCurrency(reviewItem.itemTotal),
        categoryId: reviewItem.categoryId || baseItem.categoryId || '',
      });

      const normalizedQuantity = parseQuantity(nextItem.quantity);
      const normalizedItemTotal = parseCurrency(nextItem.itemTotal);
      const normalizedUnitPrice = Number((normalizedItemTotal / normalizedQuantity).toFixed(2));
      const normalizedNextItem = {
        ...nextItem,
        quantity: normalizedQuantity,
        itemTotal: normalizedItemTotal,
        unitPrice: Number.isFinite(normalizedUnitPrice) ? normalizedUnitPrice : 0,
      };

      const updatedItems = [];
      current.items.forEach((item, itemIndex) => {
        if (itemIndex === primaryIndex) {
          updatedItems.push({ ...item, ...normalizedNextItem });
          return;
        }

        if (duplicateSet.has(itemIndex)) {
          return;
        }

        updatedItems.push(item);
      });

      return {
        ...current,
        items: updatedItems,
        ...calculateTotals(updatedItems, current.taxAmount),
      };
    });
  };

  const openReviewItemEditor = (reviewItem, index) => {
    const selectedCategory = categoriesById.get(String(reviewItem.categoryId || ''));
    const isSubcategory = Boolean(selectedCategory?.parentCategoryId);
    const parentCategoryId = isSubcategory
      ? String(selectedCategory.parentCategoryId)
      : String(selectedCategory?.id || '');
    const subcategoryId = isSubcategory ? String(selectedCategory.id) : '';

    setEditingReviewItem({
      index,
      reviewItem,
      draft: {
        itemName: toDisplayItemName(reviewItem.itemName),
        quantity: String(parseQuantity(reviewItem.quantity)),
        itemTotal: String(parseCurrency(reviewItem.itemTotal)),
        parentCategoryId,
        subcategoryId,
        categoryId: String(reviewItem.categoryId || ''),
      },
    });
  };

  const closeReviewItemEditor = () => {
    setEditingReviewItem(null);
    setShowCreateCategoryModal(false);
  };

  const handleEditorDraftChange = (field, value) => {
    setEditingReviewItem((current) => {
      if (!current) {
        return current;
      }
      return {
        ...current,
        draft: {
          ...current.draft,
          [field]: value,
        },
      };
    });
  };

  const saveReviewItemEditor = () => {
    if (!editingReviewItem?.reviewItem) {
      return;
    }

    const { reviewItem, draft } = editingReviewItem;
    const resolvedCategoryId = String(
      draft.categoryId || draft.subcategoryId || draft.parentCategoryId || ''
    ).trim();

    updateReviewItem(reviewItem, (item) => ({
      ...item,
      itemName: toDisplayItemName(draft.itemName),
      quantity: parseQuantity(draft.quantity),
      itemTotal: parseCurrency(draft.itemTotal),
      categoryId: resolvedCategoryId,
    }));
    rememberRecentCategory(resolvedCategoryId);
    setEditingReviewItem(null);
  };

  const applyCreatedCategoryToEditorDraft = (category) => {
    if (!category) {
      return;
    }

    if (category.parentCategoryId) {
      setEditingReviewItem((current) => (
        current
          ? {
              ...current,
              draft: {
                ...current.draft,
                parentCategoryId: String(category.parentCategoryId),
                subcategoryId: String(category.id),
                categoryId: String(category.id),
              },
            }
          : current
      ));
      return;
    }

    setEditingReviewItem((current) => (
      current
        ? {
            ...current,
            draft: {
              ...current.draft,
              parentCategoryId: String(category.id),
              subcategoryId: '',
              categoryId: String(category.id),
            },
          }
        : current
    ));
  };

  const applyCreatedCategoryToManualItem = (itemIndex, category) => {
    if (!Number.isInteger(itemIndex) || itemIndex < 0 || !category) {
      return;
    }

    setManualForm((current) => ({
      ...current,
      items: current.items.map((item, index) => (
        index === itemIndex ? { ...item, categoryId: String(category.id) } : item
      )),
    }));

    setManualFieldErrors((current) => {
      if (!current.items?.[itemIndex]?.categoryId) {
        return current;
      }
      return {
        ...current,
        items: {
          ...current.items,
          [itemIndex]: {
            ...current.items[itemIndex],
            categoryId: '',
          },
        },
      };
    });
  };

  const openCreateCategoryModal = (
    parentCategoryId = '',
    context = { source: 'editor', manualItemIndex: null }
  ) => {
    setNewCategoryForm({
      name: '',
      parentCategoryId: String(parentCategoryId || ''),
    });
    setNewParentSubcategories([]);
    setNewSubcategoryInput('');
    setShowAllCreateSubcategoryRefs(false);
    setError('');
    setCreateCategoryContext({
      source: context?.source || 'editor',
      manualItemIndex: Number.isInteger(context?.manualItemIndex) ? context.manualItemIndex : null,
    });
    setShowCreateCategoryModal(true);
  };

  const openCreateSubcategoryModal = () => {
    openCreateCategoryModal(
      String(editingReviewItem?.draft?.parentCategoryId || ''),
      { source: 'editor', manualItemIndex: null }
    );
  };

  const closeCreateCategoryModal = () => {
    setShowCreateCategoryModal(false);
    setIsCreateCategoryNameFocused(false);
    setIsCreateCategorySelectedFromSuggestion(false);
    setNewCategoryForm({ name: '', parentCategoryId: '' });
    setNewParentSubcategories([]);
    setNewSubcategoryInput('');
    setShowAllCreateSubcategoryRefs(false);
    setCreateCategoryContext({ source: 'editor', manualItemIndex: null });
  };

  const addCreateSubcategoryChip = (rawValue) => {
    const candidate = String(rawValue || '').trim();
    if (!candidate) {
      return;
    }
    const normalizedCandidate = toComparableText(candidate);
    setNewParentSubcategories((current) => (
      current.some((entry) => toComparableText(entry) === normalizedCandidate)
        ? current
        : [...current, candidate]
    ));
  };

  const removeCreateSubcategoryChip = (chipName) => {
    const normalizedChip = toComparableText(chipName);
    setNewParentSubcategories((current) => current.filter((entry) => toComparableText(entry) !== normalizedChip));
  };

  const handleCreateCategoryFromModal = async () => {
    const trimmedName = String(newCategoryForm.name || '').trim();
    if (!trimmedName) {
      setError('Please enter a category name');
      return;
    }

    const selectedParentCategoryId = String(newCategoryForm.parentCategoryId || '').trim() || null;
    const normalizedName = toComparableText(trimmedName);
    const childNames = [...new Set(
      [...newParentSubcategories, newSubcategoryInput]
        .map((name) => String(name || '').trim())
        .filter(Boolean)
    )];
    const hasChildNames = childNames.length > 0;
    const existingMainCategory = categories.find(
      (category) =>
        !category.parentCategoryId &&
        toComparableText(category.name) === normalizedName
    );
    const existingCategory = categories.find(
      (category) =>
        toComparableText(category.name) === normalizedName &&
        String(category.parentCategoryId || '') === String(selectedParentCategoryId || '')
    );

    if (existingCategory && !(hasChildNames && !selectedParentCategoryId)) {
      if (isCreateCategorySelectedFromSuggestion) {
        if (createCategoryContext.source === 'manual') {
          applyCreatedCategoryToManualItem(createCategoryContext.manualItemIndex, existingCategory);
        } else {
          applyCreatedCategoryToEditorDraft(existingCategory);
        }
        rememberRecentCategory(existingCategory.id);
        closeCreateCategoryModal();
        setSuccess(`Category "${existingCategory.name}" selected.`);
        return;
      }
      setError(`Category "${existingCategory.name}" already exists. Select it from the suggestion list.`);
      return;
    }

    setCreatingCategory(true);
    setError('');

    try {
      const usedColors = new Set(categories.map((category) => normalizeHexColor(category.color)));
      const createdCategories = [];
      let createdCategory = null;

      const reserveColor = () => {
        const color = getNextUniqueCategoryColor(usedColors);
        usedColors.add(normalizeHexColor(color));
        return color;
      };

      if (selectedParentCategoryId) {
        if (
          existingMainCategory &&
          String(existingMainCategory.id) !== String(selectedParentCategoryId)
        ) {
          const selectedParentName = categoriesById.get(String(selectedParentCategoryId))?.name || 'selected parent';
          setError(
            `Main category "${existingMainCategory.name}" already exists. Choose "${selectedParentName}" from Category Name suggestions or use a new name.`
          );
          setCreatingCategory(false);
          return;
        }

        createdCategory = await categoriesAPI.create({
          name: trimmedName,
          color: reserveColor(),
          parentCategoryId: selectedParentCategoryId,
        });
        createdCategories.push(createdCategory);
      } else {
        let parentCategory = null;
        if (existingMainCategory) {
          if (!hasChildNames) {
            setError(`Main category "${existingMainCategory.name}" already exists. Select it from the suggestion list.`);
            setCreatingCategory(false);
            return;
          }
          parentCategory = existingMainCategory;
        } else {
          parentCategory = await categoriesAPI.create({
            name: trimmedName,
            color: reserveColor(),
            parentCategoryId: null,
          });
          createdCategories.push(parentCategory);
        }

        createdCategory = parentCategory;
        const parentId = String(parentCategory.id);

        const existingChildNames = new Set(
          categories
            .filter((category) => String(category.parentCategoryId || '') === parentId)
            .map((category) => toComparableText(category.name))
        );
        const missingChildNames = childNames.filter((name) => !existingChildNames.has(toComparableText(name)));
        if (hasChildNames && existingMainCategory && missingChildNames.length === 0) {
          const subcategoryLabel = childNames.length === 1 ? 'subcategory' : 'subcategories';
          setError(
            `Main category "${parentCategory.name}" and ${subcategoryLabel} "${childNames.join(', ')}" already exist.`
          );
          return;
        }
        for (const childName of missingChildNames) {
          const createdChild = await categoriesAPI.create({
            name: childName,
            color: reserveColor(),
            parentCategoryId: parentId,
          });
          createdCategories.push(createdChild);
        }
      }

      if (createdCategories.length > 0) {
        setCategories((current) => [...current, ...createdCategories]);
      }
      if (createCategoryContext.source === 'manual') {
        applyCreatedCategoryToManualItem(createCategoryContext.manualItemIndex, createdCategory);
      } else {
        applyCreatedCategoryToEditorDraft(createdCategory);
      }
      rememberRecentCategory(createdCategory.id);
      if (createdCategories.length === 0) {
        setSuccess(`Using existing category "${createdCategory.name}".`);
      } else {
        const createdSubcategoryCount = Math.max(createdCategories.length - 1, 0);
        const summarySuffix = createdSubcategoryCount > 0
          ? ` with ${createdSubcategoryCount} subcategor${createdSubcategoryCount === 1 ? 'y' : 'ies'}`
          : '';
        setSuccess(`Category "${createdCategory.name}" created${summarySuffix}`);
      }
      closeCreateCategoryModal();
    } catch (createError) {
      console.error('Error creating category from item editor:', createError);
      setError(createError.response?.data?.error || 'Failed to create category');
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleTaxAmountChange = (value) => {
    setExtractedData((current) => {
      if (!current) {
        return current;
      }

      const taxAmount = parseCurrency(value);
      return {
        ...current,
        taxAmount,
        ...calculateTotals(current.items, taxAmount),
      };
    });
  };

  const handleReceiptDateChange = (date) => {
    setExtractedData((current) => {
      if (!current) {
        return current;
      }
      return {
        ...current,
        transactionDate: date ? formatDateAsMmDdYyyy(date) : '',
      };
    });
  };

  const handleSaveTransaction = async (forceDuplicateSave = false) => {
    if (!extractedData) {
      return;
    }

    const itemsToSave = buildCollapsedItems(extractedData.items);

    const hasInvalidItem = itemsToSave.some(
      (item) => !item.itemName?.trim() || parseCurrency(item.itemTotal) === 0 || !item.categoryId
    );

    if (hasInvalidItem) {
      setError('Each grouped item needs name, non-zero amount, and category before saving.');
      return;
    }

    const duplicateDetected = Boolean(extractedData?.extractionDiagnostics?.validation?.duplicateDetected);
    if (duplicateDetected && !duplicateSaveConfirmed && !forceDuplicateSave) {
      setDuplicateConfirmContext('upload');
      setShowDuplicateConfirm(true);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        transactionDate: extractedData.transactionDate,
        storeName: extractedData.storeName,
        storeLocation: extractedData.storeLocation || '',
        paymentMethod: extractedData.paymentMethod,
        taxAmount: extractedData.taxAmount,
        receiptImageUrl: extractedData.receiptImageUrl || '',
        items: itemsToSave.map((item) => ({
          itemName: toDisplayItemName(item.itemName),
          unitPrice: parseCurrency(item.unitPrice),
          quantity: parseQuantity(item.quantity),
          itemTotal: parseCurrency(item.itemTotal),
          categoryId: item.categoryId,
        })),
      };

      const result = await transactionsAPI.commitReceipt(payload);
      setBudgetSummary(result.budgetSummary || null);
      sessionStorage.removeItem(UI_DRAFT_KEY);
      setDuplicateSaveConfirmed(false);
      navigate('/transactions');
    } catch (err) {
      console.error('Error saving reviewed receipt:', err);
      setError(err.response?.data?.error || 'Failed to save reviewed receipt');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setUploadedFile(null);
    setPreview(null);
    setExtractedData(null);
    setIsProcessing(false);
    setSaving(false);
    setError('');
    setSuccess('');
    setActionFeedback('');
    setIsReceiptMetaEditing(false);
    setIsTaxEditing(false);
    setBudgetSummary(null);
    setEditingReviewItem(null);
    setShowCropModal(false);
    setImageQualityWarning('');
    sessionStorage.removeItem(UI_DRAFT_KEY);
  };

  const handleCameraCapture = async () => {
    if (isNativeMobilePlatform()) {
      try {
        const nativeFile = await captureWithNativeCamera();
        if (nativeFile) {
          await setSelectedReceiptFile(nativeFile);
          showDebouncedActionFeedback('Photo captured');
          return;
        }
      } catch (nativeCameraError) {
        console.warn('Native camera capture failed; falling back to file input:', nativeCameraError);
      }
    }

    cameraInputRef.current?.click();
  };

  const handleCameraChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    await setSelectedReceiptFile(file);
    event.target.value = '';
  };

  const handleReplaceImageClick = () => {
    setShowReplaceOptions(true);
  };

  const handleReplaceImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    await setSelectedReceiptFile(file);
    event.target.value = '';
  };

  const closeReplaceOptions = () => {
    setShowReplaceOptions(false);
  };

  const handleChooseFromDevice = async () => {
    closeReplaceOptions();
    if (isNativeMobilePlatform()) {
      try {
        const nativeFile = await pickFromNativeGallery();
        if (nativeFile) {
          await setSelectedReceiptFile(nativeFile);
          showDebouncedActionFeedback('Photo selected');
          return;
        }
      } catch (nativeGalleryError) {
        console.warn('Native gallery pick failed; falling back to file input:', nativeGalleryError);
      }
    }

    replaceInputRef.current?.click();
  };

  const handleTakePhoto = async () => {
    closeReplaceOptions();
    if (isNativeMobilePlatform()) {
      try {
        const nativeFile = await captureWithNativeCamera();
        if (nativeFile) {
          await setSelectedReceiptFile(nativeFile);
          showDebouncedActionFeedback('Photo captured');
          return;
        }
      } catch (nativeCameraError) {
        console.warn('Native camera capture failed; falling back to file input:', nativeCameraError);
      }
    }

    cameraInputRef.current?.click();
  };

  const handleStartOver = () => {
    closeReplaceOptions();
    handleCancel();
  };

  const handleRotateImage = async () => {
    if (!uploadedFile) {
      return;
    }

    try {
      const image = await loadImageElement(uploadedFile);
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        setError('Unable to rotate this image in your browser.');
        return;
      }

      canvas.width = image.height;
      canvas.height = image.width;
      context.translate(canvas.width / 2, canvas.height / 2);
      context.rotate(Math.PI / 2);
      context.drawImage(image, -image.width / 2, -image.height / 2);

      const rotatedBlob = await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Rotation failed'));
            return;
          }
          resolve(blob);
        }, 'image/jpeg', OUTPUT_JPEG_QUALITY);
      });

      const rotatedFile = new File([rotatedBlob], String(uploadedFile.name || 'receipt.jpg').replace(/\.[^.]+$/, '.jpg'), {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
      await setSelectedReceiptFile(rotatedFile, { autoOpenCrop: false });
      setSuccess('Image rotated.');
    } catch (rotationError) {
      console.error('Error rotating image:', rotationError);
      setError('Failed to rotate image.');
    }
  };

  const openCropModal = () => {
    if (!uploadedFile || !preview) {
      return;
    }
    setCropRect(null);
    setCropDisplay({ width: 0, height: 0 });
    setShowCropModal(true);
  };

  const closeCropModal = () => {
    setShowCropModal(false);
    setCropRect(null);
    cropDragRef.current = null;
  };

  const handleCropImageLoad = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const width = Math.round(bounds.width);
    const height = Math.round(bounds.height);
    setCropDisplay({
      width,
      height,
    });
    setCropRect((current) => {
      if (current && current.width > 0 && current.height > 0) {
        return current;
      }
      const insetX = Math.max(12, Math.round(width * 0.06));
      const insetY = Math.max(12, Math.round(height * 0.06));
      return {
        x: insetX,
        y: insetY,
        width: Math.max(24, width - insetX * 2),
        height: Math.max(24, height - insetY * 2),
      };
    });
  };

  const startCropInteraction = (event, mode) => {
    if (!cropSurfaceRef.current) {
      return;
    }
    const bounds = cropSurfaceRef.current.getBoundingClientRect();
    const pointerX = clamp(event.clientX - bounds.left, 0, bounds.width);
    const pointerY = clamp(event.clientY - bounds.top, 0, bounds.height);
    const baseRect = cropRect || {
      x: 0,
      y: 0,
      width: bounds.width,
      height: bounds.height,
    };
    if (event.currentTarget?.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    cropDragRef.current = {
      mode,
      startX: pointerX,
      startY: pointerY,
      bounds,
      startRect: baseRect,
    };
  };

  const handleCropPointerDown = (event) => {
    if (!cropSurfaceRef.current || !cropRect) {
      return;
    }
    const bounds = cropSurfaceRef.current.getBoundingClientRect();
    const pointerX = clamp(event.clientX - bounds.left, 0, bounds.width);
    const pointerY = clamp(event.clientY - bounds.top, 0, bounds.height);
    if (!isPointInsideRect(pointerX, pointerY, cropRect)) {
      return;
    }
    startCropInteraction(event, 'move');
  };

  const handleCropHandlePointerDown = (event, mode) => {
    event.stopPropagation();
    event.preventDefault();
    startCropInteraction(event, mode);
  };

  const handleCropPointerMove = (event) => {
    if (!cropDragRef.current) {
      return;
    }

    const { startX, startY, bounds, startRect, mode } = cropDragRef.current;
    const currentX = clamp(event.clientX - bounds.left, 0, bounds.width);
    const currentY = clamp(event.clientY - bounds.top, 0, bounds.height);
    const deltaX = currentX - startX;
    const deltaY = currentY - startY;
    const minSize = 24;

    if (mode === 'move') {
      const nextX = clamp(startRect.x + deltaX, 0, bounds.width - startRect.width);
      const nextY = clamp(startRect.y + deltaY, 0, bounds.height - startRect.height);
      setCropRect({
        x: nextX,
        y: nextY,
        width: startRect.width,
        height: startRect.height,
      });
      return;
    }

    let left = startRect.x;
    let top = startRect.y;
    let right = startRect.x + startRect.width;
    let bottom = startRect.y + startRect.height;

    if (mode.includes('w')) {
      left = clamp(startRect.x + deltaX, 0, right - minSize);
    }
    if (mode.includes('e')) {
      right = clamp(startRect.x + startRect.width + deltaX, left + minSize, bounds.width);
    }
    if (mode.includes('n')) {
      top = clamp(startRect.y + deltaY, 0, bottom - minSize);
    }
    if (mode.includes('s')) {
      bottom = clamp(startRect.y + startRect.height + deltaY, top + minSize, bounds.height);
    }

    setCropRect({
      x: left,
      y: top,
      width: right - left,
      height: bottom - top,
    });
  };

  const handleCropPointerUp = () => {
    cropDragRef.current = null;
  };

  const applyCrop = async () => {
    if (!uploadedFile || !cropRect || cropRect.width < 8 || cropRect.height < 8 || !cropImageRef.current) {
      setError('Select a crop area first.');
      return;
    }

    try {
      const previewImage = cropImageRef.current;
      const naturalWidth = previewImage.naturalWidth;
      const naturalHeight = previewImage.naturalHeight;
      const displayWidth = previewImage.clientWidth;
      const displayHeight = previewImage.clientHeight;
      if (!naturalWidth || !naturalHeight || !displayWidth || !displayHeight) {
        setError('Unable to crop this image.');
        return;
      }

      const scaleX = naturalWidth / displayWidth;
      const scaleY = naturalHeight / displayHeight;
      const sx = Math.max(0, Math.floor(cropRect.x * scaleX));
      const sy = Math.max(0, Math.floor(cropRect.y * scaleY));
      const sw = Math.max(1, Math.floor(cropRect.width * scaleX));
      const sh = Math.max(1, Math.floor(cropRect.height * scaleY));

      const sourceImage = await loadImageElement(uploadedFile);
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        setError('Cropping is not supported in this browser.');
        return;
      }
      canvas.width = sw;
      canvas.height = sh;
      context.drawImage(sourceImage, sx, sy, sw, sh, 0, 0, sw, sh);

      const croppedBlob = await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Crop export failed'));
            return;
          }
          resolve(blob);
        }, 'image/jpeg', OUTPUT_JPEG_QUALITY);
      });

      const croppedFile = new File(
        [croppedBlob],
        String(uploadedFile.name || 'receipt.jpg').replace(/\.[^.]+$/, '-crop.jpg'),
        {
          type: 'image/jpeg',
          lastModified: Date.now(),
        }
      );

      await setSelectedReceiptFile(croppedFile, { autoOpenCrop: false });
      closeCropModal();
      showDebouncedActionFeedback('Crop applied.');
    } catch (cropError) {
      console.error('Failed to crop image:', cropError);
      setError('Failed to crop image.');
    }
  };

  useEffect(() => {
    setDuplicateSaveConfirmed(false);
    setShowDuplicateConfirm(false);
    setDuplicateConfirmContext('upload');
  }, [extractedData]);

  const manualItemsSubtotal = useMemo(
    () => manualForm.items.reduce((sum, item) => sum + toNumber(resolveManualItemTotal(item)), 0),
    [manualForm.items]
  );

  const manualTaxAmount = useMemo(
    () => {
      const parsed = Number(manualForm.taxAmount);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    },
    [manualForm.taxAmount]
  );

  const manualGrandTotal = useMemo(
    () => Number((manualItemsSubtotal + manualTaxAmount).toFixed(2)),
    [manualItemsSubtotal, manualTaxAmount]
  );

  const resetManualForm = () => {
    setManualForm({
      transactionDate: getTodayIsoDate(),
      storeName: '',
      storeLocation: '',
      taxAmount: '',
      paymentMethod: '',
      isRecurring: false,
      recurrenceFrequency: 'monthly',
      recurrenceStartDate: '',
      recurrenceEndDate: '',
      items: [createManualItem()],
    });
    setManualFormError('');
    setManualFieldErrors(createEmptyManualFieldErrors());
    setIsManualDetailsOpen(false);
  };

  const openManualModal = () => {
    setShowManualModal(true);
    setIsManualDetailsOpen(false);
    setError('');
    setSuccess('');
    setBudgetSummary(null);
    setManualFormError('');
    setManualFieldErrors(createEmptyManualFieldErrors());
  };

  const closeManualModal = () => {
    setShowManualModal(false);
    resetManualForm();
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && showManualModal) {
        closeManualModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showManualModal]);

  useEffect(() => {
    if (showManualModal && manualModalRef.current) {
      manualModalRef.current.scrollTop = 0;
    }
  }, [showManualModal]);

  const handleManualFormChange = (field, value) => {
    setManualForm((current) => ({ ...current, [field]: value }));
    setManualFieldErrors((current) => ({
      ...current,
      [field]: '',
    }));
  };

  const handleManualItemChange = (index, field, value) => {
    setManualForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => (
        itemIndex === index
          ? (() => {
              const nextItem = { ...item, [field]: value };
              if (field === 'itemTotal') {
                nextItem.itemTotalOverridden = true;
              } else if (field === 'quantity' || field === 'unitPrice') {
                const recalculatedTotal = getAutoCalculatedItemTotal(nextItem);
                nextItem.itemTotalOverridden = false;
                nextItem.itemTotal = recalculatedTotal !== null ? String(recalculatedTotal) : '';
              }
              return nextItem;
            })()
          : item
      )),
    }));
    setManualFieldErrors((current) => {
      const nextItems = { ...(current.items || {}) };
      if (nextItems[index]) {
        nextItems[index] = { ...nextItems[index], [field]: '' };
      }
      return {
        ...current,
        items: nextItems,
      };
    });
  };

  const handleQuickManualItemChange = (field, value) => {
    setManualForm((current) => {
      const nextItems = Array.isArray(current.items) && current.items.length > 0
        ? [...current.items]
        : [createManualItem()];
      const nextItem = {
        ...nextItems[0],
        [field]: value,
      };
      if (field === 'itemTotal') {
        nextItem.itemTotalOverridden = true;
      } else if (field === 'quantity' || field === 'unitPrice') {
        const recalculatedTotal = getAutoCalculatedItemTotal(nextItem);
        nextItem.itemTotalOverridden = false;
        nextItem.itemTotal = recalculatedTotal !== null ? String(recalculatedTotal) : '';
      }
      nextItems[0] = nextItem;
      return {
        ...current,
        items: nextItems,
      };
    });
    setManualFieldErrors((current) => {
      const nextItems = { ...(current.items || {}) };
      if (nextItems[0]) {
        nextItems[0] = {
          ...nextItems[0],
          [field]: '',
        };
      }
      return {
        ...current,
        items: nextItems,
      };
    });
  };

  const addManualItem = () => {
    setManualForm((current) => ({
      ...current,
      items: [...current.items, createManualItem()],
    }));
  };

  const handleEditorCategoryDropdownChange = (selectedValue) => {
    const normalizedSelectedValue = String(selectedValue || '');
    if (normalizedSelectedValue === ADD_PARENT_CATEGORY_OPTION) {
      openCreateCategoryModal('', { source: 'editor', manualItemIndex: null });
      return;
    }

    setEditingReviewItem((current) => {
      if (!current) {
        return current;
      }

      if (!normalizedSelectedValue) {
        return {
          ...current,
          draft: {
            ...current.draft,
            parentCategoryId: '',
            subcategoryId: '',
            categoryId: '',
          },
        };
      }

      const selectedCategory = categoriesById.get(normalizedSelectedValue);
      const isSubcategory = Boolean(selectedCategory?.parentCategoryId);
      const parentCategoryId = isSubcategory
        ? String(selectedCategory.parentCategoryId)
        : String(selectedCategory?.id || '');
      const subcategoryId = isSubcategory ? String(selectedCategory.id) : '';

      return {
        ...current,
        draft: {
          ...current.draft,
          parentCategoryId,
          subcategoryId,
          categoryId: normalizedSelectedValue,
        },
      };
    });
  };

  const removeManualItem = (index) => {
    setManualForm((current) => ({
      ...current,
      items: current.items.filter((_, itemIndex) => itemIndex !== index),
    }));
    setManualFieldErrors((current) => {
      const existingItems = current.items || {};
      const nextItems = {};
      Object.keys(existingItems).forEach((key) => {
        const numericKey = Number(key);
        if (!Number.isInteger(numericKey) || numericKey === index) {
          return;
        }
        const nextKey = numericKey > index ? numericKey - 1 : numericKey;
        nextItems[nextKey] = existingItems[key];
      });
      return {
        ...current,
        items: nextItems,
      };
    });
  };

  const manualTransactionDate = manualForm.transactionDate
    ? new Date(`${manualForm.transactionDate}T00:00:00`)
    : null;
  const quickManualItem = manualForm.items?.[0] || createManualItem();
  const manualRecurrenceStartDate = manualForm.recurrenceStartDate
    ? new Date(`${manualForm.recurrenceStartDate}T00:00:00`)
    : null;
  const manualRecurrenceEndDate = manualForm.recurrenceEndDate
    ? new Date(`${manualForm.recurrenceEndDate}T00:00:00`)
    : null;

  const handleManualDateChange = (date) => {
    if (!date) {
      handleManualFormChange('transactionDate', '');
      return;
    }

    const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
    const localIsoDate = new Date(date.getTime() - timezoneOffsetMs).toISOString().split('T')[0];
    setManualForm((current) => {
      const shouldSyncRecurrenceStart =
        current.isRecurring &&
        (!current.recurrenceStartDate || current.recurrenceStartDate === current.transactionDate);

      return {
        ...current,
        transactionDate: localIsoDate,
        recurrenceStartDate: shouldSyncRecurrenceStart ? localIsoDate : current.recurrenceStartDate,
      };
    });
    setManualFieldErrors((current) => ({
      ...current,
      transactionDate: '',
    }));
  };

  const handleManualRecurrenceStartDateChange = (date) => {
    if (!date) {
      handleManualFormChange('recurrenceStartDate', '');
      return;
    }

    const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
    const localIsoDate = new Date(date.getTime() - timezoneOffsetMs).toISOString().split('T')[0];
    setManualForm((current) => ({
      ...current,
      recurrenceStartDate: localIsoDate,
      recurrenceEndDate:
        current.recurrenceEndDate && current.recurrenceEndDate < localIsoDate
          ? ''
          : current.recurrenceEndDate,
    }));
  };

  const handleManualRecurrenceEndDateChange = (date) => {
    if (!date) {
      handleManualFormChange('recurrenceEndDate', '');
      return;
    }

    const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
    const localIsoDate = new Date(date.getTime() - timezoneOffsetMs).toISOString().split('T')[0];
    handleManualFormChange('recurrenceEndDate', localIsoDate);
  };

  const handleManualRecurringToggle = (enabled) => {
    setManualForm((current) => {
      if (!enabled) {
        return {
          ...current,
          isRecurring: false,
          recurrenceFrequency: 'monthly',
          recurrenceStartDate: '',
          recurrenceEndDate: '',
        };
      }

      return {
        ...current,
        isRecurring: true,
        recurrenceFrequency: current.recurrenceFrequency || 'monthly',
        recurrenceStartDate: current.recurrenceStartDate || current.transactionDate,
      };
    });
  };

  const manualRecurrencePreview = useMemo(() => {
    if (!manualForm.isRecurring || !manualForm.recurrenceFrequency || !manualForm.recurrenceStartDate) {
      return '';
    }

    const frequencyLabel = RECURRENCE_FREQUENCY_LABELS[manualForm.recurrenceFrequency];
    const formattedStart = formatRecurrencePreviewDate(manualForm.recurrenceStartDate);
    if (!frequencyLabel || !formattedStart) {
      return '';
    }

    const formattedEnd = manualForm.recurrenceEndDate
      ? formatRecurrencePreviewDate(manualForm.recurrenceEndDate)
      : '';
    if (formattedEnd) {
      return `Repeats ${frequencyLabel.toLowerCase()} from ${formattedStart} until ${formattedEnd}.`;
    }
    return `Repeats ${frequencyLabel.toLowerCase()} starting ${formattedStart}.`;
  }, [
    manualForm.isRecurring,
    manualForm.recurrenceEndDate,
    manualForm.recurrenceFrequency,
    manualForm.recurrenceStartDate,
  ]);

  const saveManualTransaction = async (forceDuplicateSave = false) => {
    setManualFormError('');
    setError('');
    setSuccess('');
    const nextFieldErrors = createEmptyManualFieldErrors();

    if (
      !manualForm.transactionDate ||
      !manualForm.storeName.trim() ||
      !manualForm.paymentMethod
    ) {
      if (!manualForm.transactionDate) {
        nextFieldErrors.transactionDate = 'Transaction date is required.';
      }
      if (!manualForm.storeName.trim()) {
        nextFieldErrors.storeName = 'Store name is required.';
      }
      if (!manualForm.paymentMethod) {
        nextFieldErrors.paymentMethod = 'Payment method is required.';
      }
      setManualFieldErrors(nextFieldErrors);
      setManualFormError('Please fill all required fields.');
      return;
    }

    if (!Array.isArray(manualForm.items) || manualForm.items.length === 0) {
      setManualFormError('Add at least one item.');
      return;
    }

    const normalizedItems = [];
    for (let index = 0; index < manualForm.items.length; index += 1) {
      const item = manualForm.items[index];
      const fallbackQuickItemName = index === 0 && normalizedItems.length === 0
        ? String(manualForm.storeName || '').trim()
        : '';
      const itemName = String(item.itemName || '').trim() || fallbackQuickItemName;
      const categoryId = String(item.categoryId || '').trim();
      const itemTotal = resolveManualItemTotal(item);
      const quantity = item.quantity === '' ? 1 : Number(item.quantity);
      const unitPrice = item.unitPrice === '' ? undefined : Number(item.unitPrice);

      if (!itemName || !categoryId || !Number.isFinite(itemTotal) || itemTotal <= 0) {
        nextFieldErrors.items[index] = {
          itemName: !itemName ? 'Item name is required.' : '',
          categoryId: !categoryId ? 'Category is required.' : '',
          itemTotal: !Number.isFinite(itemTotal) || itemTotal <= 0 ? 'Enter a valid item total.' : '',
        };
        setManualFieldErrors(nextFieldErrors);
        setManualFormError(`Item ${index + 1}: enter item name, category, and either item total or quantity + unit price.`);
        return;
      }

      if (!Number.isFinite(quantity) || quantity <= 0) {
        nextFieldErrors.items[index] = {
          ...(nextFieldErrors.items[index] || {}),
          quantity: 'Quantity must be greater than 0.',
        };
        setManualFieldErrors(nextFieldErrors);
        setManualFormError(`Item ${index + 1}: quantity must be greater than 0.`);
        return;
      }

      if (unitPrice !== undefined && (!Number.isFinite(unitPrice) || unitPrice < 0)) {
        nextFieldErrors.items[index] = {
          ...(nextFieldErrors.items[index] || {}),
          unitPrice: 'Unit price cannot be negative.',
        };
        setManualFieldErrors(nextFieldErrors);
        setManualFormError(`Item ${index + 1}: unit price cannot be negative.`);
        return;
      }

      normalizedItems.push({
        itemName,
        categoryId,
        itemTotal,
        quantity,
        unitPrice,
      });
    }

    const taxAmount = manualForm.taxAmount === '' ? undefined : Number(manualForm.taxAmount);
    if (taxAmount !== undefined && (!Number.isFinite(taxAmount) || taxAmount < 0)) {
      nextFieldErrors.taxAmount = 'Tax amount cannot be negative.';
      setManualFieldErrors(nextFieldErrors);
      setManualFormError('Tax amount cannot be negative.');
      return;
    }

    const normalizedRecurrenceFrequency = String(manualForm.recurrenceFrequency || '').trim().toLowerCase();
    if (manualForm.isRecurring) {
      const validRecurrenceFrequency = RECURRENCE_FREQUENCY_OPTIONS.some(
        (option) => option.value === normalizedRecurrenceFrequency
      );
      if (!validRecurrenceFrequency) {
        setManualFormError('Select a valid recurring frequency.');
        return;
      }
      if (!manualForm.recurrenceStartDate) {
        setManualFormError('Recurring transactions require a start date.');
        return;
      }
      if (
        manualForm.recurrenceEndDate &&
        manualForm.recurrenceEndDate < manualForm.recurrenceStartDate
      ) {
        setManualFormError('Recurring end date must be on or after the start date.');
        return;
      }
    }

    setManualFieldErrors(createEmptyManualFieldErrors());

    try {
      setCreatingTransaction(true);
      for (let index = 0; index < normalizedItems.length; index += 1) {
        const item = normalizedItems[index];
        await transactionsAPI.create({
          transactionDate: manualForm.transactionDate,
          storeName: manualForm.storeName.trim(),
          storeLocation: manualForm.storeLocation.trim() || undefined,
          paymentMethod: manualForm.paymentMethod,
          taxAmount: index === 0 ? taxAmount : undefined,
          itemName: toDisplayItemName(item.itemName),
          itemTotal: item.itemTotal,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          categoryId: item.categoryId,
          receiptTotal: manualGrandTotal,
          receiptTaxAmount: parseCurrency(taxAmount || 0),
          checkDuplicate: index === 0,
          forceDuplicateSave: forceDuplicateSave && index === 0,
          isRecurring: manualForm.isRecurring,
          recurrenceFrequency: manualForm.isRecurring ? normalizedRecurrenceFrequency : undefined,
          recurrenceStartDate: manualForm.isRecurring ? manualForm.recurrenceStartDate : undefined,
          recurrenceEndDate:
            manualForm.isRecurring && manualForm.recurrenceEndDate
              ? manualForm.recurrenceEndDate
              : undefined,
        });
      }

      closeManualModal();
      const manualTotal = normalizedItems.reduce((sum, item) => sum + parseCurrency(item.itemTotal), 0) + parseCurrency(taxAmount || 0);
      setSaveSummary({
        title: 'Manual Transactions Saved',
        transactionCount: normalizedItems.length,
        totalAmount: Number(manualTotal.toFixed(2)),
        budgetRemaining: null,
      });
      if (manualForm.isRecurring) {
        setSuccess('Recurring transaction schedule saved successfully.');
      }
      sessionStorage.removeItem(UI_DRAFT_KEY);
    } catch (err) {
      console.error('Error creating manual transaction:', err);
      if (
        err?.response?.status === 409 &&
        err?.response?.data?.duplicateDetected &&
        !forceDuplicateSave
      ) {
        setDuplicateConfirmContext('manual');
        setShowDuplicateConfirm(true);
        return;
      }
      setManualFormError(err.response?.data?.error || 'Failed to create transaction');
    } finally {
      setCreatingTransaction(false);
    }
  };

  const handleManualItemCategorySelect = async (index, selectedValue) => {
    if (selectedValue !== ADD_PARENT_CATEGORY_OPTION) {
      handleManualItemChange(index, 'categoryId', selectedValue);
      return;
    }

    openCreateCategoryModal('', { source: 'manual', manualItemIndex: index });
  };

  const submitManualTransaction = async (event) => {
    event.preventDefault();
    await saveManualTransaction(false);
  };

  const extractionValidation = extractedData?.extractionDiagnostics?.validation || null;
  const requiresReview = Boolean(extractionValidation?.requiresReview);

  return (
    <div className="space-y-4 md:space-y-6 pb-4">
      {!extractedData && (
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900">Upload Receipt</h1>
          </div>
        </div>
      )}

      {isProcessing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/35 px-3 backdrop-blur-[1px]">
          <div className="w-full max-w-md rounded-xl border border-white/70 bg-white/90 px-3 py-4 shadow-xl">
            <p className="text-center text-sm font-semibold text-gray-900">Processing receipt</p>
            <div className="relative mt-4 px-6">
              <div className="absolute left-6 right-6 top-5 h-1 rounded-full bg-gray-200" />
              <div
                className="absolute left-6 top-5 h-1 rounded-full bg-blue-500 transition-all duration-500 ease-out"
                style={{
                  width:
                    processingStage === 'uploading'
                      ? '24%'
                      : processingStage === 'extracting'
                        ? '58%'
                        : processingStage === 'categorizing'
                          ? '92%'
                          : '12%',
                }}
              />
              <div
                className="absolute top-[18px] h-3 w-3 rounded-full border-2 border-white bg-blue-600 shadow transition-all duration-500 ease-out"
                style={{
                  left:
                    processingStage === 'uploading'
                      ? 'calc(1.5rem + (100% - 3rem) * 0.24 - 0.375rem)'
                      : processingStage === 'extracting'
                        ? 'calc(1.5rem + (100% - 3rem) * 0.58 - 0.375rem)'
                        : processingStage === 'categorizing'
                          ? 'calc(1.5rem + (100% - 3rem) * 0.92 - 0.375rem)'
                          : 'calc(1.5rem + (100% - 3rem) * 0.12 - 0.375rem)',
                }}
              />
              <div className="relative grid grid-cols-3 gap-2">
                {[
                  { id: 'uploading', label: 'Upload' },
                  { id: 'extracting', label: 'Extract' },
                  { id: 'categorizing', label: 'Categorize' },
                ].map((stage, index) => {
                  const order = { uploading: 1, extracting: 2, categorizing: 3 };
                  const current = order[processingStage] || 0;
                  const stageOrder = order[stage.id];
                  const complete = current > stageOrder;
                  const active = current === stageOrder;
                  return (
                    <div key={stage.id} className="flex flex-col items-center text-center">
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold transition-all duration-300 ${
                          active
                            ? 'border-4 border-blue-500 bg-white text-blue-600'
                            : complete
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-200 text-gray-500'
                        }`}
                      >
                        {complete ? 'OK' : index + 1}
                      </div>
                      <p className={`mt-2 text-[11px] font-medium ${active || complete ? 'text-gray-900' : 'text-gray-500'}`}>
                        {stage.label}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      {pendingDraft && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/80 px-4 py-3 text-sm text-blue-800">
          <p className="font-medium">Saved draft found</p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={handleRestoreDraft}
              className="rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
            >
              Restore Draft
            </button>
            <button
              type="button"
              onClick={handleDiscardDraft}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Discard
            </button>
          </div>
        </div>
      )}
      
      {budgetSummary && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/80 px-4 py-3 text-sm text-blue-700">
          Budget {budgetSummary.month}/{budgetSummary.year}: {formatCurrency(budgetSummary.totalSpent)} spent.
        </div>
      )}

      {extractedData && extractionValidation && requiresReview && !extractionValidation.mathIntegrityOk && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="font-medium">Requires Review</p>
          <p className="mt-1 text-xs">
            Math {extractionValidation.mathIntegrityOk ? 'OK' : 'Mismatch'}
            {Number.isFinite(Number(extractionValidation.mathDelta)) ? ` (${formatCurrency(extractionValidation.mathDelta)})` : ''} |{' '}
            {extractionValidation.duplicateDetected ? 'Possible duplicate' : 'No duplicate'}
          </p>
        </div>
      )}

      {!categoriesLoading && categories.length === 0 && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          No categories found. Create at least one category first.
        </div>
      )}

      <div className={`grid grid-cols-1 gap-4 md:gap-6 ${extractedData ? '' : 'lg:grid-cols-[420px_1fr]'}`}>
        {!extractedData && (
          <section className="h-fit space-y-3 rounded-3xl bg-gradient-to-br from-sky-50 via-white to-cyan-50 p-3 shadow-sm sm:p-4 sm:space-y-4">

          {!uploadedFile ? (
            <div className="space-y-3">
              <div
                {...getRootProps()}
                className={`cursor-pointer rounded-2xl border p-6 text-center transition sm:p-8 ${
                  isDragActive
                    ? 'border-blue-500 bg-blue-50/70'
                    : 'border-dashed border-sky-300 bg-white/80 hover:border-blue-400'
                }`}
              >
                <input {...getInputProps()} />
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-blue-100 text-blue-700">
                  <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V6m0 0-4 4m4-4 4 4M5 16v2a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2" />
                  </svg>
                </div>
                <p className="mt-3 text-sm font-semibold text-gray-900">Drop or tap to upload</p>
                <p className="mt-1 text-xs text-gray-500">JPG/PNG/WEBP • Max 1 file</p>
              </div>

              <button
                onClick={handleCameraCapture}
                type="button"
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Use Camera
              </button>

              <button
                type="button"
                onClick={openManualModal}
                disabled={categoriesLoading || categories.length === 0}
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
              >
                Add Transaction Detail
              </button>

            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs font-medium text-gray-600">
                {isProcessing ? 'Processing receipt...' : 'Looks good. Ready to process'}
              </p>
              {!extractedData && (
                <div className="relative">
                  <img src={preview} alt="Receipt preview" className="w-full rounded-2xl shadow-sm" />
                </div>
              )}

              {!extractedData && imageQualityWarning && (
                <div className="flex flex-wrap items-center gap-2">
                  {buildImageQualityBadges(imageQualityWarning).map((badge) => (
                    <span
                      key={badge}
                      className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700"
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <div className="rounded-2xl border border-gray-200 bg-white/90 p-2">
                  <p className="px-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">Image Tools</p>
                  <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <button
                      type="button"
                      onClick={handleCameraCapture}
                      disabled={isProcessing}
                      className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <span className="inline-grid h-4 w-4 place-items-center rounded-full bg-gray-100 text-[9px] font-bold">C</span>
                      Use Camera
                    </button>
                    <button
                      type="button"
                      onClick={openCropModal}
                      disabled={isProcessing}
                      className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <span className="inline-grid h-4 w-4 place-items-center rounded-full bg-gray-100 text-[9px] font-bold">CR</span>
                      Crop
                    </button>
                    <button
                      type="button"
                      onClick={handleRotateImage}
                      disabled={isProcessing}
                      className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <span className="inline-grid h-4 w-4 place-items-center rounded-full bg-gray-100 text-[9px] font-bold">R</span>
                      Rotate
                    </button>
                    <button
                      type="button"
                      onClick={handleReplaceImageClick}
                      disabled={isProcessing}
                      className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <span className="inline-grid h-4 w-4 place-items-center rounded-full bg-gray-100 text-[9px] font-bold">RP</span>
                      Replace
                    </button>
                  </div>
                </div>
                {actionFeedback && (
                  <p
                    aria-live="polite"
                    className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700"
                  >
                    {actionFeedback}
                  </p>
                )}
              </div>

              {!isProcessing && !extractedData && (
                <>
                  <button
                    onClick={handleProcessReceipt}
                    disabled={categoriesLoading}
                    className="hidden sm:block w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    Process Receipt
                  </button>
                  <div className="fixed inset-x-0 bottom-16 z-30 px-3 sm:hidden">
                    <button
                      onClick={handleProcessReceipt}
                      disabled={categoriesLoading}
                      className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-lg hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      Process Receipt
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <input
            ref={replaceInputRef}
            type="file"
            accept="image/*"
            onChange={handleReplaceImageChange}
            className="hidden"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleCameraChange}
            className="hidden"
          />
          </section>
        )}

        {extractedData && (
          <section className="rounded-3xl border border-gray-200 bg-slate-50/80 p-3 shadow-sm sm:p-6">
            <div className="space-y-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">Step 2 of 3</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900 sm:text-xl lg:text-2xl">Review and Save</h2>
                <p className="mt-1 text-xs text-slate-700 sm:text-sm lg:text-base">
                  Confirm the extracted details from your uploaded receipt.
                </p>
              </div>

              <div className="relative rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                {isReceiptMetaEditing ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Merchant</label>
                      <input
                        type="text"
                        value={extractedData.storeName}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        onChange={(e) => setExtractedData({ ...extractedData, storeName: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Location</label>
                      <input
                        type="text"
                        value={extractedData.storeLocation}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        onChange={(e) => setExtractedData({ ...extractedData, storeLocation: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Date</label>
                      <DatePicker
                        selected={parseMmDdYyyyToDate(extractedData.transactionDate)}
                        onChange={handleReceiptDateChange}
                        dateFormat="MM-dd-yyyy"
                        withPortal
                        portalId="root"
                        placeholderText="MM-DD-YYYY"
                        wrapperClassName="w-full"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Payment</label>
                      <select
                        value={extractedData.paymentMethod}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        onChange={(e) => setExtractedData({ ...extractedData, paymentMethod: e.target.value })}
                      >
                        <option>Credit Card</option>
                        <option>Debit Card</option>
                        <option>Cash</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 text-lg font-semibold text-blue-700">
                        {String(extractedData.storeName || '?').trim().slice(0, 1).toUpperCase() || '?'}
                      </div>
                      <div className="min-w-0 pr-14 sm:pr-0">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Merchant</p>
                        <p className="text-base font-semibold leading-tight text-slate-900 sm:text-lg lg:text-xl">
                          {extractedData.storeName || 'Not set'}
                        </p>
                        <p className="mt-1 text-sm text-slate-700 sm:text-base">{extractedData.storeLocation || 'Location not set'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 border-t border-slate-200 pt-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Date</p>
                        <p className="mt-1 text-sm font-semibold leading-tight text-slate-900 sm:text-base lg:text-xl">
                          {formatDateForDisplay(extractedData.transactionDate) || 'Not set'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Payment</p>
                        <p className="mt-1 text-sm font-semibold leading-tight text-slate-900 sm:text-base lg:text-xl">
                          {extractedData.paymentMethod || 'Not set'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setIsReceiptMetaEditing((current) => !current)}
                  className="absolute right-4 top-4 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  {isReceiptMetaEditing ? 'Done' : 'Edit'}
                </button>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
                  <p className="text-sm font-semibold leading-none text-slate-900 sm:text-xl lg:text-2xl">Items</p>
                  <div className="flex items-center">
                    <span className="text-xs text-slate-600 sm:text-sm lg:text-base">{reviewItems.length} items</span>
                  </div>
                </div>

                <div>
                  {reviewItems.map((item, index) => (
                    <div
                      key={`item-${index}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => openReviewItemEditor(item, index)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openReviewItemEditor(item, index);
                        }
                      }}
                      className="cursor-pointer border-b border-slate-100 px-4 py-3 transition hover:bg-blue-50/40 last:border-b-0"
                    >
                      <div className="grid grid-cols-[2.75rem_minmax(0,1fr)] gap-3 sm:grid-cols-[3.25rem_minmax(0,1fr)] sm:items-center">
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-xs font-semibold text-slate-600 sm:h-12 sm:w-12 sm:text-sm">
                          {String(item.itemName || '?').trim().slice(0, 2).toUpperCase() || '?'}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="truncate text-xs font-semibold leading-tight text-slate-900 sm:text-base lg:text-xl">
                              {toDisplayItemName(item.itemName) || 'Untitled item'}
                            </p>
                            <p className="shrink-0 text-base font-normal leading-none text-slate-900 sm:text-lg lg:text-2xl">
                              {formatCurrency(item.itemTotal)}
                            </p>
                          </div>
                          <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-slate-600 sm:text-xs lg:text-xs">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`inline-flex rounded-full border px-2 py-0.5 ${
                                item.categoryId
                                  ? 'border-slate-300 bg-slate-100 text-slate-700'
                                  : 'border-amber-300 bg-amber-50 text-amber-700'
                              }`}>
                                {categoryDisplayNameById.get(String(item.categoryId)) || 'Category needed'}
                              </span>
                              <span>Qty: {parseQuantity(item.quantity)}</span>
                            </div>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                openReviewItemEditor(item, index);
                              }}
                              className="shrink-0 rounded-md p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                              aria-label="Edit item"
                              title="Edit item"
                            >
                              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.1 2.1 0 1 1 2.971 2.971L8.75 17.542 4 19l1.458-4.75L16.862 3.487z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl px-2 pt-1">
                <div className="flex flex-col items-end gap-2 text-right">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-700">Total Amount</p>
                  <p className="text-2xl font-normal leading-none text-blue-700 sm:text-3xl lg:text-4xl">
                    {formatCurrency(extractedData.total)}
                  </p>
                  <div className="flex items-center justify-end gap-2 text-xs italic text-slate-600 sm:text-sm lg:text-sm">
                    <span>Includes {formatCurrency(extractedData.taxAmount)} tax</span>
                    <button
                      type="button"
                      onClick={() => setIsTaxEditing((current) => !current)}
                      className="rounded-md p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-800"
                      aria-label={isTaxEditing ? 'Done editing tax' : 'Edit tax'}
                      title={isTaxEditing ? 'Done' : 'Edit tax'}
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.1 2.1 0 1 1 2.971 2.971L8.75 17.542 4 19l1.458-4.75L16.862 3.487z" />
                      </svg>
                    </button>
                  </div>
                  {isTaxEditing && (
                    <div className="flex items-center justify-end gap-2">
                      <label className="text-xs text-slate-500">Tax</label>
                      <input
                        type="number"
                        step="0.01"
                        value={extractedData.taxAmount}
                        className="w-24 rounded-md border border-gray-300 px-2 py-1.5 text-sm text-right"
                        onChange={(e) => handleTaxAmountChange(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={handleSaveTransaction}
                disabled={saving}
                className="w-full rounded-full bg-blue-700 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-800 disabled:bg-gray-400 sm:px-6 sm:py-3.5 sm:text-base lg:py-4 lg:text-lg"
              >
                {saving ? 'Saving...' : 'Save and Update Budget'}
              </button>

              <button
                type="button"
                onClick={handleCancel}
                className="mx-auto block text-sm font-medium text-slate-700 hover:text-slate-900 sm:text-sm lg:text-base"
              >
                Discard and Start Over
              </button>
            </div>
          </section>
        )}
      </div>

      {editingReviewItem && (
        <div className="fixed inset-0 z-[120] flex items-start justify-center overflow-hidden bg-slate-900/45 p-0 backdrop-blur-[2px] sm:items-center sm:p-4">
          <div className="flex h-[100dvh] w-full max-w-lg flex-col overflow-y-auto bg-gradient-to-b from-slate-50 to-white shadow-2xl sm:my-2 sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl sm:border sm:border-gray-200">
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Edit Item</h3>
              <button
                type="button"
                onClick={closeReviewItemEditor}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">Update details and category before saving.</p>
            </div>

            <div className="grid grid-cols-2 gap-3 px-5 pb-6 pt-4 sm:pb-28">
              <div className="col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Item</label>
                <input
                  type="text"
                  value={editingReviewItem.draft.itemName}
                  onChange={(event) => handleEditorDraftChange('itemName', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Qty</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={editingReviewItem.draft.quantity}
                  onChange={(event) => handleEditorDraftChange('quantity', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-blue-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editingReviewItem.draft.itemTotal}
                  onChange={(event) => handleEditorDraftChange('itemTotal', event.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-blue-400 focus:outline-none"
                />
              </div>
              <div className="col-span-2 rounded-2xl border border-gray-200 bg-white p-3">
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">Category</label>

                <div className="mt-2">
                  <SearchableSelect
                    value={String(editingReviewItem.draft.categoryId || '')}
                    onChange={handleEditorCategoryDropdownChange}
                    options={categorySelectOptions}
                    placeholder="Search category"
                    ariaLabel="Search category for review item"
                    selectedLabelOverride={
                      categoryDisplayNameById.get(String(editingReviewItem.draft.categoryId || '')) || ''
                    }
                    inputClassName="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-400 focus:outline-none"
                    menuClassName="z-[130] max-h-56"
                  />
                  <button
                    type="button"
                    onClick={openCreateSubcategoryModal}
                    className="mt-2 w-full rounded border border-blue-200 bg-blue-50 px-3 py-2 text-left text-xs font-medium text-blue-700 hover:bg-blue-100"
                  >
                    + Add Category
                  </button>
                </div>

                {recentCategoryChips.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {recentCategoryChips.map((category) => (
                      <button
                        key={`recent-${category.id}`}
                        type="button"
                        onClick={() => handleEditorCategoryDropdownChange(String(category.id))}
                        className="rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
                      >
                        {categoryDisplayNameById.get(String(category.id)) || category.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-1 flex flex-col-reverse gap-2 border-t border-slate-200 bg-white px-5 py-3 pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:sticky sm:bottom-0 sm:bg-white/95 sm:shadow-[0_-8px_20px_rgba(15,23,42,0.08)] sm:backdrop-blur sm:flex-row sm:justify-end sm:pb-[env(safe-area-inset-bottom)]">
              <button
                type="button"
                onClick={closeReviewItemEditor}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveReviewItemEditor}
                disabled={
                  !String(editingReviewItem.draft.itemName || '').trim() ||
                  parseCurrency(editingReviewItem.draft.itemTotal) === 0 ||
                  !String(
                    editingReviewItem.draft.categoryId
                    || editingReviewItem.draft.subcategoryId
                    || editingReviewItem.draft.parentCategoryId
                    || ''
                  ).trim()
                }
                className="w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-gray-400 sm:w-auto"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateCategoryModal && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/55 backdrop-blur-[2px] p-4">
          <div className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h3 className="text-base font-semibold text-gray-900">Add New Category</h3>
              <button
                type="button"
                onClick={closeCreateCategoryModal}
                className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <div className="space-y-3 overflow-y-auto px-4 py-3">
              {error && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Category Name</label>
                <div className="relative">
                  <input
                    type="text"
                    value={newCategoryForm.name}
                    onFocus={() => setIsCreateCategoryNameFocused(true)}
                    onBlur={() => setIsCreateCategoryNameFocused(false)}
                    onChange={(event) => {
                      setIsCreateCategorySelectedFromSuggestion(false);
                      setShowAllCreateSubcategoryRefs(false);
                      setNewCategoryForm((current) => ({ ...current, name: event.target.value }));
                    }}
                    placeholder="Type new main category or select existing"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  {isCreateCategoryNameFocused && createCategoryNameSuggestions.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white p-1 shadow-lg">
                      {createCategoryNameSuggestions.map((category) => (
                        <button
                          key={`existing-main-upload-${category.id}`}
                          type="button"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            setIsCreateCategorySelectedFromSuggestion(true);
                            setShowAllCreateSubcategoryRefs(false);
                            setNewCategoryForm((current) => ({ ...current, name: String(category.name || '') }));
                            setIsCreateCategoryNameFocused(false);
                          }}
                          className="w-full rounded-md px-2.5 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {!newCategoryForm.parentCategoryId && matchedMainCategoryForCreate && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-2.5">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Existing Subcategories</p>
                  {existingCreateSubcategories.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {visibleCreateSubcategoryRefs.map((subcategory) => (
                        <span
                          key={`existing-subcategory-ref-upload-${subcategory.id}`}
                          className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-700"
                        >
                          {subcategory.name}
                        </span>
                      ))}
                      {hiddenCreateSubcategoryRefCount > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowAllCreateSubcategoryRefs((current) => !current)}
                          className="rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-100"
                        >
                          {showAllCreateSubcategoryRefs ? 'Show less' : `+${hiddenCreateSubcategoryRefCount} more`}
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">No subcategories yet.</p>
                  )}
                </div>
              )}

              {!newCategoryForm.parentCategoryId && (
                <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-2.5">
                  <p className="mb-2 text-sm font-medium text-blue-900">Optional Subcategories</p>
                  <div className="flex min-h-11 flex-wrap items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 py-1.5">
                    {newParentSubcategories.map((name) => (
                      <span key={`subcategory-chip-upload-${name}`} className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
                        {name}
                        <button
                          type="button"
                          onClick={() => removeCreateSubcategoryChip(name)}
                          className="text-blue-700 hover:text-blue-900"
                          aria-label={`Remove ${name}`}
                        >
                          x
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={newSubcategoryInput}
                      onChange={(event) => setNewSubcategoryInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ',') {
                          event.preventDefault();
                          addCreateSubcategoryChip(newSubcategoryInput);
                          setNewSubcategoryInput('');
                        }
                      }}
                      onBlur={() => {
                        addCreateSubcategoryChip(newSubcategoryInput);
                        setNewSubcategoryInput('');
                      }}
                      placeholder={newParentSubcategories.length > 0 ? 'Add another subcategory' : 'Type subcategory and press Enter'}
                      className="min-w-[160px] flex-1 border-0 px-1 py-1 text-sm focus:outline-none"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 mt-1 flex items-center justify-end gap-3 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur">
              <button
                type="button"
                onClick={closeCreateCategoryModal}
                className="text-sm font-medium text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateCategoryFromModal}
                disabled={creatingCategory}
                className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-gray-400"
              >
                {creatingCategory ? 'Creating...' : 'Save Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ManualTransactionModal
        show={showManualModal}
        modalRef={manualModalRef}
        onClose={closeManualModal}
        onSubmit={submitManualTransaction}
        manualFormError={manualFormError}
        manualFieldErrors={manualFieldErrors}
        isMobileView={isManualMobileView}
        isManualDetailsOpen={isManualDetailsOpen}
        setIsManualDetailsOpen={setIsManualDetailsOpen}
        manualForm={manualForm}
        quickManualItem={quickManualItem}
        handleManualFormChange={handleManualFormChange}
        handleQuickManualItemChange={handleQuickManualItemChange}
        handleManualItemCategorySelect={handleManualItemCategorySelect}
        categorySelectOptions={categorySelectOptions}
        quickCategoryAriaLabel="Search category for quick item"
        itemCategoryAriaLabelPrefix="Search category for manual item"
        manualTransactionDate={manualTransactionDate}
        handleManualDateChange={handleManualDateChange}
        manualRecurrenceStartDate={manualRecurrenceStartDate}
        handleManualRecurrenceStartDateChange={handleManualRecurrenceStartDateChange}
        manualRecurrenceEndDate={manualRecurrenceEndDate}
        handleManualRecurrenceEndDateChange={handleManualRecurrenceEndDateChange}
        handleManualRecurringToggle={handleManualRecurringToggle}
        recurrenceFrequencyOptions={RECURRENCE_FREQUENCY_OPTIONS}
        manualRecurrencePreview={manualRecurrencePreview}
        handleManualItemChange={handleManualItemChange}
        removeManualItem={removeManualItem}
        addManualItem={addManualItem}
        resolveManualItemTotal={resolveManualItemTotal}
        manualItemsSubtotal={manualItemsSubtotal}
        manualTaxAmount={manualTaxAmount}
        manualGrandTotal={manualGrandTotal}
        formatCurrency={formatCurrency}
        creatingTransaction={creatingTransaction}
      />

      {showCropModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 backdrop-blur-[2px] p-2 sm:items-center sm:p-4">
          <div className="flex max-h-[calc(100dvh-1rem)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl sm:max-h-[calc(100dvh-2rem)]">
            <div className="flex shrink-0 items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Crop Receipt</h3>
              <button
                type="button"
                onClick={closeCropModal}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <div className="mt-3 min-h-0 flex-1 rounded-xl border border-gray-200 bg-gray-50 p-2">
              <div className="mx-auto h-full w-full overflow-auto rounded-lg bg-black/80 p-2">
                <div
                  ref={cropSurfaceRef}
                  className="relative mx-auto w-fit"
                  onPointerDown={handleCropPointerDown}
                  onPointerMove={handleCropPointerMove}
                  onPointerUp={handleCropPointerUp}
                  onPointerLeave={handleCropPointerUp}
                  style={{ touchAction: 'none' }}
                >
                  <img
                    ref={cropImageRef}
                    src={preview || ''}
                    alt="Crop receipt"
                    onLoad={handleCropImageLoad}
                    className="block max-h-[52dvh] w-auto select-none sm:max-h-[65vh]"
                    draggable={false}
                  />

                  {cropRect && cropRect.width > 0 && cropRect.height > 0 && (
                    <div
                      className="absolute border-2 border-blue-500 bg-blue-400/20 shadow-[0_0_0_9999px_rgba(0,0,0,0.38)] cursor-move"
                      style={{
                        left: cropRect.x,
                        top: cropRect.y,
                        width: cropRect.width,
                        height: cropRect.height,
                      }}
                    />
                  )}

                  {cropRect && cropRect.width > 0 && cropRect.height > 0 && (
                    <>
                      <button
                        type="button"
                        onPointerDown={(event) => handleCropHandlePointerDown(event, 'n')}
                        className="absolute -translate-x-1/2 -translate-y-1/2 h-3 w-8 rounded-full bg-blue-500 shadow"
                        style={{ left: cropRect.x + cropRect.width / 2, top: cropRect.y, cursor: 'ns-resize' }}
                        aria-label="Resize top"
                      />
                      <button
                        type="button"
                        onPointerDown={(event) => handleCropHandlePointerDown(event, 's')}
                        className="absolute -translate-x-1/2 -translate-y-1/2 h-3 w-8 rounded-full bg-blue-500 shadow"
                        style={{ left: cropRect.x + cropRect.width / 2, top: cropRect.y + cropRect.height, cursor: 'ns-resize' }}
                        aria-label="Resize bottom"
                      />
                      <button
                        type="button"
                        onPointerDown={(event) => handleCropHandlePointerDown(event, 'w')}
                        className="absolute -translate-x-1/2 -translate-y-1/2 h-8 w-3 rounded-full bg-blue-500 shadow"
                        style={{ left: cropRect.x, top: cropRect.y + cropRect.height / 2, cursor: 'ew-resize' }}
                        aria-label="Resize left"
                      />
                      <button
                        type="button"
                        onPointerDown={(event) => handleCropHandlePointerDown(event, 'e')}
                        className="absolute -translate-x-1/2 -translate-y-1/2 h-8 w-3 rounded-full bg-blue-500 shadow"
                        style={{ left: cropRect.x + cropRect.width, top: cropRect.y + cropRect.height / 2, cursor: 'ew-resize' }}
                        aria-label="Resize right"
                      />
                      <button
                        type="button"
                        onPointerDown={(event) => handleCropHandlePointerDown(event, 'nw')}
                        className="absolute -translate-x-1/2 -translate-y-1/2 h-4 w-4 rounded-full border border-white bg-blue-600 shadow"
                        style={{ left: cropRect.x, top: cropRect.y, cursor: 'nwse-resize' }}
                        aria-label="Resize top left"
                      />
                      <button
                        type="button"
                        onPointerDown={(event) => handleCropHandlePointerDown(event, 'ne')}
                        className="absolute -translate-x-1/2 -translate-y-1/2 h-4 w-4 rounded-full border border-white bg-blue-600 shadow"
                        style={{ left: cropRect.x + cropRect.width, top: cropRect.y, cursor: 'nesw-resize' }}
                        aria-label="Resize top right"
                      />
                      <button
                        type="button"
                        onPointerDown={(event) => handleCropHandlePointerDown(event, 'sw')}
                        className="absolute -translate-x-1/2 -translate-y-1/2 h-4 w-4 rounded-full border border-white bg-blue-600 shadow"
                        style={{ left: cropRect.x, top: cropRect.y + cropRect.height, cursor: 'nesw-resize' }}
                        aria-label="Resize bottom left"
                      />
                      <button
                        type="button"
                        onPointerDown={(event) => handleCropHandlePointerDown(event, 'se')}
                        className="absolute -translate-x-1/2 -translate-y-1/2 h-4 w-4 rounded-full border border-white bg-blue-600 shadow"
                        style={{ left: cropRect.x + cropRect.width, top: cropRect.y + cropRect.height, cursor: 'nwse-resize' }}
                        aria-label="Resize bottom right"
                      />
                    </>
                  )}
                </div>
              </div>

              {cropRect && (
                <p className="mt-2 text-xs text-gray-600">
                  Selection: {Math.round(cropRect.width)} x {Math.round(cropRect.height)} px
                  {cropDisplay.width > 0 && cropDisplay.height > 0
                    ? ` (view ${cropDisplay.width} x ${cropDisplay.height})`
                    : ''}
                </p>
              )}
            </div>

            <div className="sticky bottom-0 mt-4 flex shrink-0 justify-end gap-2 border-t border-gray-200 bg-white/95 px-1 pt-3 pb-[max(env(safe-area-inset-bottom),0.5rem)]">
              <button
                type="button"
                onClick={closeCropModal}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyCrop}
                className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
              >
                Apply Crop
              </button>
            </div>
          </div>
        </div>
      )}

      {showSubcategorySuggestionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 backdrop-blur-[2px] p-4">
          <div className="w-full max-w-2xl rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Suggested Subcategories</h3>
              <button
                type="button"
                onClick={() => setShowSubcategorySuggestionModal(false)}
                className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <p className="mt-2 text-sm text-gray-600">
              Review suggestions below. Only selected rows will be created/assigned.
            </p>

            <div className="mt-4 max-h-[55vh] overflow-y-auto space-y-2">
              {subcategorySuggestions.map((entry) => (
                <div key={entry.id} className="rounded-lg border border-gray-200 p-3">
                  <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_auto] gap-2 items-center">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={Boolean(entry.selected)}
                        onChange={(event) => updateSubcategorySuggestion(entry.id, { selected: event.target.checked })}
                      />
                      Use
                    </label>
                    <input
                      type="text"
                      value={entry.name}
                      onChange={(event) => updateSubcategorySuggestion(entry.id, { name: event.target.value })}
                      className="rounded border border-gray-300 px-2.5 py-1.5 text-sm"
                      placeholder="Subcategory name"
                    />
                    <SearchableSelect
                      value={String(entry.parentCategoryId || '')}
                      onChange={(selectedValue) => updateSubcategorySuggestion(entry.id, { parentCategoryId: selectedValue })}
                      options={parentCategoryReferenceOptions}
                      placeholder="Search parent category"
                      ariaLabel="Search parent category for suggested subcategory"
                      inputClassName="w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-400 focus:outline-none"
                      menuClassName="max-h-52"
                    />
                    <p className="text-xs text-gray-500 text-right">
                      {entry.itemIndexes.length} item{entry.itemIndexes.length === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowSubcategorySuggestionModal(false)}
                className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applySubcategorySuggestions}
                disabled={applyingSubcategorySuggestions}
                className="rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:bg-blue-400"
              >
                {applyingSubcategorySuggestions ? 'Applying...' : 'Apply Selected'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 backdrop-blur-[2px] p-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-gray-900">Possible Duplicate Receipt</h3>
            <p className="mt-2 text-sm text-gray-600">
              This looks similar to a saved receipt.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDuplicateConfirm(false)}
                className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDuplicateConfirm(false);
                  if (duplicateConfirmContext === 'manual') {
                    saveManualTransaction(true);
                    return;
                  }
                  setDuplicateSaveConfirmed(true);
                  handleSaveTransaction(true);
                }}
                className="rounded bg-amber-600 px-3 py-2 text-sm text-white hover:bg-amber-700"
              >
                Save Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {showReplaceOptions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 backdrop-blur-[2px] p-4">
          <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-gray-900">Replace Image</h3>
            <div className="mt-4 space-y-2">
              <button
                type="button"
                onClick={handleChooseFromDevice}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Choose from Device
              </button>
              <button
                type="button"
                onClick={handleTakePhoto}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Take Photo
              </button>
              <button
                type="button"
                onClick={handleStartOver}
                className="w-full rounded-xl border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
              >
                Start Over
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={closeReplaceOptions}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {saveSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 backdrop-blur-[2px] p-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-gray-900">{saveSummary.title}</h3>
            <div className="mt-3 space-y-1 text-sm text-gray-700">
              <p>Transactions created: <span className="font-medium text-gray-900">{saveSummary.transactionCount}</span></p>
              <p>Total amount: <span className="font-medium text-gray-900">{formatCurrency(saveSummary.totalAmount)}</span></p>
              {saveSummary.budgetRemaining !== null && (
                <p>
                  Budget remaining: <span className="font-medium text-gray-900">{formatCurrency(saveSummary.budgetRemaining)}</span>
                </p>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSaveSummary(null)}
                className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Stay Here
              </button>
              <button
                type="button"
                onClick={() => {
                  setSaveSummary(null);
                  navigate('/transactions');
                }}
                className="rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
              >
                View Transactions
              </button>
            </div>
          </div>
        </div>
      )}

      <StatusToast
        error={error}
        success={success}
        onClearError={() => setError('')}
        onClearSuccess={() => setSuccess('')}
      />
    </div>
  );
}

export default UploadReceipt;

